/**
 * Centralized API service layer.
 *
 * All frontend data-fetching goes through these helpers so that:
 *   - Base URL, headers, timeout and error handling are consistent
 *   - Auth tokens are attached automatically when available
 *   - Callers receive a uniform { data, error, status, meta } shape
 *
 * Canonical rules:
 *   - Public/customer routes go through apiFetch(...)
 *   - Admin routes MUST go through adminFetch(...) same-origin proxy
 *   - New portfolio customer path is GET /api/portfolio/positions
 */

import { supabase } from "../lib/supabaseClient";
import { API_BASE_URL } from "../config/apiConfig";
import {
  buildAgentWorkChatPayload,
  V0_DESIGN_CONTEXT_TIMEOUT_MS,
  V0_FULL_SYSTEM_CONTEXT_TIMEOUT_MS,
} from "../components/shared/agent/work/agentWorkChatLogic.js";

// ── Configuration ──────────────────────────────────────────────────────
const API_BASE = API_BASE_URL;
const DEFAULT_TIMEOUT_MS = 10_000;
// Heavy admin aggregation endpoints (e.g. operator-summary) can take 20–25 s
// when the backend performs cross-service fan-out queries.
const HEAVY_ADMIN_TIMEOUT_MS = 25_000;
// Medium admin drilldown endpoints (service-diagnostics, data-flow-health,
// db-capacity) may run for up to 20 s.
const MEDIUM_ADMIN_TIMEOUT_MS = 20_000;
// Die Route antwortet fire-and-forget sofort mit 202 (der Job läuft im
// Hintergrund weiter, siehe hqs-backend PR #1162) – ein kurzer Timeout reicht,
// wir warten nur auf die Sofort-Bestätigung, nicht auf das Ergebnis.
const AUTONOMOUS_SCAN_TIMEOUT_MS = MEDIUM_ADMIN_TIMEOUT_MS;
// Agent/LLM requests (Gemini, DeepSeek) can take 60–90 s on complex prompts.
// On Vercel the BFF proxy (api/admin-proxy.js) enforces a 55 s ceiling, so
// AGENT_TIMEOUT_MS must exceed that so the browser's AbortController never
// fires before the proxy has a chance to return a response.
// On Railway the Gemini routes are handled directly (no proxy hop), so the
// 90 s value is a safe guard-rail against indefinitely stalled connections.
const AGENT_TIMEOUT_MS = 90_000;
// v0 design agent uses longer timeouts: backend allows 90 s for design_context
// and 150 s for full_system_context. Frontend must not abort before those.
// V0_AGENT_TIMEOUT_MS is the conservative ceiling used when no explicit
// contextType is present in the request payload (covers full_system_context).
const V0_AGENT_TIMEOUT_MS = 170_000;

// ── Helpers ────────────────────────────────────────────────────────────

/** Get Supabase access token (if signed in). */
async function getAccessToken() {
  try {
    const result = await supabase?.auth?.getSession?.();
    return result?.data?.session?.access_token || "";
  } catch {
    return "";
  }
}

/** Build standard headers, optionally including an auth bearer token. */
function buildHeaders(accessToken) {
  const headers = { Accept: "application/json" };
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  return headers;
}

/**
 * Extract proxy diagnostic fields from a parsed error response body.
 * These fields are set by api/admin-proxy.js (Vercel) and server.js (Railway)
 * when the upstream fetch is aborted due to a timeout.
 */
const PROXY_DIAG_FIELDS = [
  "frontendProxyTimeoutMs",
  "frontendRequestDurationMs",
  "frontendTimedOutAt",
  "frontendAbortTriggered",
  "frontendReceivedResponse",
  "frontendReceivedBody",
];
function extractProxyDiagnostics(parsedBody) {
  if (!parsedBody) return {};
  return Object.fromEntries(
    PROXY_DIAG_FIELDS
      .filter((k) => parsedBody[k] != null)
      .map((k) => [k, parsedBody[k]]),
  );
}

/**
 * Generic fetch wrapper with timeout, JSON parsing and error handling.
 *
 * @param {string} path
 * @param {object} [options]
 * @param {number} [timeoutMs]
 * @returns {Promise<{ data: any|null, error: string|null, status: number, meta: object }>}
 */
export async function apiFetch(path, options = {}, timeoutMs = DEFAULT_TIMEOUT_MS) {
  // Internal HQS routes (admin/market/runtime/guardian/user/portfolio/
  // watchlist/frontend-backend-contract) MUST always go same-origin, even if
  // the caller forgot to pass `{ sameOrigin: true }` and even if
  // API_BASE_URL/VITE_API_BASE_URL is configured to point at Railway. This is
  // the single choke point that prevents any internal route from ever being
  // dispatched directly from the browser to an upstream origin.
  const sameOrigin = Boolean(options._sameOrigin) || isInternalHqsRoute(path);
  // When same-origin is enforced, normalize away any accidental absolute
  // URL/host so the request can never leak to a different origin.
  const requestPath = sameOrigin ? (normalizeSameOriginAdminPath(path) || path) : path;
  // Ignore API_BASE for same-origin requests so they can never bypass the
  // proxy even if VITE_API_BASE_URL is set.
  const base = sameOrigin ? "" : API_BASE;
  const url = `${base}${requestPath}`;
  // Internal HQS routes are authenticated by the caller's own Supabase
  // session (owner e-mail/password login) — the server proxies forward it to
  // Railway and never substitute a shared key. Attach it whenever the caller
  // did not pass one explicitly, so no internal call goes out without it.
  const accessToken = options._accessToken !== undefined
    ? options._accessToken
    : (isInternalHqsRoute(path) ? await getAccessToken() : "");
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(url, {
      signal: controller.signal,
      // Include credentials (cookies) for same-origin requests. This is
      // critical for admin-bypass cookie authentication to work, and it never
      // sends credentials cross-origin.
      credentials: sameOrigin ? "same-origin" : undefined,
      ...options,
      headers: {
        ...buildHeaders(accessToken || ""),
        ...options.headers,
      },
    });

    if (!res.ok) {
      // 501 means endpoint explicitly not implemented yet
      if (res.status === 501) {
        return {
          data: null,
          error: null,
          status: 501,
          meta: { notImplemented: true },
        };
      }

      // 401/403 from an admin endpoint means proxy/backend auth problem
      // (classified upstream as admin_auth_failed).
      if (res.status === 401 || res.status === 403) {
        return {
          data: null,
          error: null,
          status: res.status,
          meta: { adminUnauthorized: true },
        };
      }

      // 404 on admin (same-origin proxy) = endpoint not yet deployed on backend
      // (route_missing_or_not_deployed). Treat silently so optional admin
      // endpoints do not trigger global error banners – callers check
      // meta.notFound or the null data to decide UX. Optional cockpit callers
      // also receive a small unavailable data object so they can distinguish
      // missing endpoints from empty successful responses.
      if (res.status === 404 && sameOrigin) {
        return {
          data: { unavailable: true, status: 404 },
          error: null,
          status: 404,
          meta: { notFound: true, unavailable: true },
        };
      }

      let text = "";
      try { text = await res.text(); } catch { /* ignore body-read errors */ }
      // Try to parse structured error body (may contain safetyLocked, proxy diagnostics, etc.)
      let parsedBody = null;
      try { parsedBody = text ? JSON.parse(text) : null; } catch { /* plain text */ }
      // Extract the most meaningful error message from the structured body.
      const errorMsg =
        parsedBody?.error ||
        `HTTP ${res.status}: ${text}`.trim();
      return {
        data: null,
        error: errorMsg,
        status: res.status,
        meta: {
          ...(parsedBody?.safetyLocked ? { safetyLocked: true } : {}),
          // Forward the backend's own error classification when present
          // (e.g. rate_limit/provider_timeout/provider_unavailable/
          // provider_error/safety_violation/invalid_request) so callers can
          // distinguish a provider-side failure from a plain auth/transport
          // failure instead of collapsing everything to the HTTP status.
          ...(typeof parsedBody?.errorCategory === "string" && parsedBody.errorCategory
            ? { errorCategory: parsedBody.errorCategory }
            : {}),
          // Forward proxy diagnostics when present (set by admin-proxy on timeout).
          ...extractProxyDiagnostics(parsedBody),
        },
      };
    }

    const data = await res.json();
    return { data, error: null, status: res.status, meta: {} };
  } catch (e) {
    if (e?.name === "AbortError") {
      return {
        data: null,
        error: "Anfrage hat das Zeitlimit überschritten.",
        status: 408,
        meta: { timeout: true, endpoint: path },
      };
    }

    return {
      data: null,
      error: String(e?.message || "Netzwerkfehler"),
      status: 0,
      meta: {},
    };
  } finally {
    // The timeout also covers reading the response body, not only headers.
    clearTimeout(timer);
  }
}

/**
 * Resolve the current Supabase session's access token for authenticatedFetch().
 *
 * Deliberately never caches a token across calls — it re-reads
 * supabase.auth.getSession() on every invocation, so a token refreshed in
 * the background by the Supabase client's own autoRefreshToken (see
 * src/lib/supabaseClient.js) is always picked up on the very next call.
 *
 * `sessionKnown: false` means the session status itself could not be
 * determined (the Supabase client isn't configured, or getSession() itself
 * failed) — this is distinct from a definitively resolved "no session"
 * (logged out), which reports `sessionKnown: true, token: null`. Either
 * case means authenticatedFetch() must not start the network request.
 */
async function resolveSessionAccessToken() {
  try {
    const result = await supabase?.auth?.getSession?.();
    if (!result || result.error) {
      return { token: null, sessionKnown: false };
    }
    const token = result?.data?.session?.access_token || "";
    return { token: token || null, sessionKnown: true };
  } catch {
    return { token: null, sessionKnown: false };
  }
}

/**
 * Central helper for every browser call that requires the caller's own
 * Supabase session (critical summary/readiness, user portfolio/watchlist,
 * notifications). This is the single place that:
 *   - loads the current session via getSession() (transparently refreshed
 *     by the Supabase client when needed — see resolveSessionAccessToken())
 *   - attaches it as `Authorization: Bearer <token>` exactly once
 *   - never starts the underlying request while the session status is
 *     unknown or the session is absent/expired — it fails closed with a
 *     synthesized 401 result instead, in the exact same shape apiFetch
 *     already returns for a real backend 401 (`{ data: null, error: null,
 *     status: 401, meta: { adminUnauthorized: true } }`), so existing
 *     callers need no special-casing
 *   - always forces same-origin dispatch (these are internal HQS routes)
 *
 * @param {string} path
 * @param {object} [options] - forwarded to apiFetch unchanged (method,
 *   body, signal, cache, etc.). Any caller-supplied Authorization header is
 *   stripped first so the resolved session token is always the single
 *   source of truth and is never set twice.
 * @param {number} [timeoutMs]
 */
export async function authenticatedFetch(path, options = {}, timeoutMs = DEFAULT_TIMEOUT_MS) {
  const { token, sessionKnown } = await resolveSessionAccessToken();

  if (!sessionKnown || !token) {
    return {
      data: null,
      error: null,
      status: 401,
      meta: { adminUnauthorized: true, noSession: true, sessionKnown },
    };
  }

  const { headers: callerHeaders, ...restOptions } = options;
  const headers = { ...(callerHeaders || {}) };
  delete headers.Authorization;
  delete headers.authorization;

  return apiFetch(path, { ...restOptions, headers, _accessToken: token, _sameOrigin: true }, timeoutMs);
}

/** Build a query string from an object, omitting null/undefined/empty-string values. */
function buildQuery(params = {}) {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (typeof value === "string" && value.trim() === "") return;
    query.set(key, String(value));
  });

  const qs = query.toString();
  return qs ? `?${qs}` : "";
}

// ── Internal HQS route enforcement ──────────────────────────────────────
//
// Security invariant: ALL internal HQS backend/admin/runtime routes MUST be
// called same-origin (relative URL, credentials: "same-origin") and MUST
// NEVER be routed directly from the browser to Railway (or any other
// upstream), regardless of API_BASE_URL / VITE_API_BASE_URL configuration.
// The admin key (ADMIN_API_KEY / HQS_ADMIN_KEY / x-admin-key) is attached
// ONLY server-side (Vercel API routes / server proxy) – it must never exist
// in browser code, browser bundles, or a VITE_ADMIN_API_KEY env var.
//
// This detection is centralized here so individual call-sites do not need to
// remember to pass `{ sameOrigin: true }` themselves: any path matching one
// of these prefixes is forced same-origin unconditionally inside apiFetch.
const INTERNAL_HQS_ROUTE_PREFIXES = Object.freeze([
  "/api/admin/",
  "/api/admin-", // e.g. /api/admin-bypass-status, /api/admin-random-calculated
  "/api/market",
  "/api/runtime-",
  "/api/frontend-backend-contract",
  "/api/guardian",
  "/api/user/",
  "/api/portfolio",
  "/api/watchlist",
  "/api/notifications",
]);

/**
 * Returns true when `path` targets one of HQS's internal backend/admin/
 * runtime routes and must therefore always be same-origin.
 * Accepts either a bare path ("/api/admin/foo") or a path plus query string.
 */
export function isInternalHqsRoute(path) {
  if (typeof path !== "string" || !path.trim()) return false;
  // Strip any accidental scheme/host so absolute URLs pointing at our own
  // API routes are still recognized (they will be normalized to a relative
  // path below); this never allows routing to a *different* origin though.
  let pathname = path.trim();
  if (/^https?:\/\//i.test(pathname)) {
    try {
      pathname = new URL(pathname).pathname;
    } catch {
      return false;
    }
  }
  return INTERNAL_HQS_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

function normalizeSameOriginAdminPath(path) {
  if (typeof path !== "string" || !path.trim()) return null;
  const value = path.trim();
  if (value.startsWith("/")) return value;
  if (/^https?:\/\//i.test(value)) {
    try {
      const base =
        typeof window !== "undefined" && window.location?.origin
          ? window.location.origin
          : "http://localhost";
      const url = new URL(value, base);
      if (typeof window !== "undefined" && window.location?.origin && url.origin !== window.location.origin) {
        return null;
      }
      return `${url.pathname}${url.search}`;
    } catch {
      return null;
    }
  }
  return value.startsWith("api/") ? `/${value}` : `/${value.replace(/^\/+/, "")}`;
}

// ── Public/customer-facing endpoint wrappers ───────────────────────────

/** Fetch live market data. */
export async function fetchMarket() {
  return apiFetch("/api/market");
}

/** Fetch canonical customer portfolio positions view. */
export async function fetchPortfolioPositions(params = {}) {
  const query = buildQuery({
    status: params.status ?? "open",
    limit: params.limit ?? 50,
    offset: params.offset ?? 0,
    sort: params.sort ?? "symbol",
    direction: params.direction ?? "asc",

    includeNews: params.includeNews ?? true,
    includeContext: params.includeContext ?? true,
    includeScore: params.includeScore ?? true,
    includeSignals: params.includeSignals ?? true,
    includeOutlook: params.includeOutlook ?? true,
    includeAgents: params.includeAgents ?? true,
    includeCompare: params.includeCompare ?? true,
  });

  return apiFetch(`/api/portfolio/positions${query}`);
}

/** Fetch portfolio overview alias (same backend family, smaller future UI entrypoint). */
export async function fetchPortfolioOverview(params = {}) {
  const query = buildQuery({
    status: params.status ?? "open",
  });

  return apiFetch(`/api/portfolio/overview${query}`);
}

/**
 * Match an uploaded portfolio against the calculated HQS universe.
 *
 * Accepts either a bare symbol list or richer positions (with shares /
 * entryPrice).  The backend returns `{ covered, missing, coverageRate }`
 * where covered entries include HQS score, price and last calculation time.
 */
export async function fetchPortfolioMatch(input) {
  const body =
    Array.isArray(input) && input.length > 0 && typeof input[0] === "object"
      ? { positions: input }
      : { symbols: Array.isArray(input) ? input : [] };

  return apiFetch("/api/portfolio/match", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/** Fetch guardian status (requires auth when backend expects it). */
export async function fetchGuardian() {
  const token = await getAccessToken();
  return apiFetch("/api/guardian", { _accessToken: token });
}

/** Fetch market news for given symbols (comma-separated string or array). */
export async function fetchMarketNews(symbols) {
  const normalized = Array.isArray(symbols) ? symbols.join(",") : symbols;
  if (!normalized) {
    return { data: null, error: "Keine Symbole angegeben.", status: 0, meta: {} };
  }
  return apiFetch(`/api/market-news?symbols=${encodeURIComponent(normalized)}`);
}

/** Stock lookup (detailed info). */
export async function fetchStockLookup(symbol) {
  return apiFetch(`/api/stock-lookup?symbol=${encodeURIComponent(symbol)}`);
}

/** Symbol auto-suggest. */
export async function fetchSymbolSuggest(query) {
  return apiFetch(`/api/symbol-suggest?query=${encodeURIComponent(query)}`);
}

/** Insider signals for given symbols. */
export async function fetchInsiderSignal(symbols) {
  return apiFetch(`/api/insider-signal?symbols=${encodeURIComponent(symbols)}`);
}

/** Sentiment & correlation for a single symbol. */
export async function fetchSentimentCorrelation(symbol) {
  return apiFetch(`/api/sentiment-correlation?symbol=${encodeURIComponent(symbol)}`);
}

/** AI copilot brief. */
export async function fetchAiCopilotBrief(body) {
  const token = await getAccessToken();
  return apiFetch("/api/ai-copilot-brief", {
    method: "POST",
    _accessToken: token,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/** Portfolio sync (Alpaca / CSV import). */
export async function fetchPortfolioSync(body) {
  const token = await getAccessToken();
  return apiFetch("/api/portfolio-sync", {
    method: "POST",
    _accessToken: token,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/** Create Stripe checkout session. */
export async function fetchCreateCheckoutSession(body) {
  const token = await getAccessToken();
  return apiFetch("/api/create-checkout-session", {
    method: "POST",
    _accessToken: token,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

// ── Notification & escalation layer ───────────────────────────────────

async function notificationFetch(path, options = {}, timeoutMs = DEFAULT_TIMEOUT_MS) {
  return authenticatedFetch(path, options, timeoutMs);
}

export async function fetchNotificationConfig() {
  return notificationFetch("/api/notifications/config", { cache: "no-store" });
}

export async function fetchNotifications(limit = 100) {
  const safeLimit = Math.min(200, Math.max(1, Number(limit) || 100));
  return notificationFetch(`/api/notifications?limit=${safeLimit}`, { cache: "no-store" });
}

export async function fetchNotificationUserState() {
  return notificationFetch("/api/notifications/user-state", { cache: "no-store" });
}

function notificationMutation(path, body) {
  return notificationFetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function markNotificationRead(notificationId) {
  return notificationMutation("/api/notifications/mark-read", { notificationId });
}

export async function markNotificationSeen(notificationId) {
  return notificationMutation("/api/notifications/mark-seen", { notificationId });
}

export async function markNotificationActed(notificationId, responseType = "opened") {
  return notificationMutation("/api/notifications/mark-acted", { notificationId, responseType });
}

export async function markNotificationDismissed(notificationId) {
  return notificationMutation("/api/notifications/mark-dismissed", { notificationId });
}

export async function registerNotificationDevice(subscription) {
  return notificationMutation("/api/notifications/register-device", {
    token: JSON.stringify(subscription),
    deviceType: "web",
  });
}

// ── Admin endpoints ────────────────────────────────────────────────────

// Cache and in-flight requests belong to exactly one bearer session. A late
// result keeps its old cache object and cannot populate a new session's cache.
let _adminRequestCache = { token: null, inFlight: new Map(), recent: new Map() };
const DEDUP_CACHE_TTL_MS = 2000;

export async function adminFetch(path, timeoutMs = DEFAULT_TIMEOUT_MS, options = {}) {
  const token = await getAccessToken();
  if (_adminRequestCache.token !== token) {
    _adminRequestCache = { token, inFlight: new Map(), recent: new Map() };
  }
  const cache = _adminRequestCache;
  const request = () => apiFetch(path, {
    ...options,
    _accessToken: token,
    _sameOrigin: true,
    headers: { ...options.headers },
  }, timeoutMs);
  const method = (options.method || "GET").toUpperCase();
  // Custom headers and abort signals must keep their own request semantics.
  if (method !== "GET" || !token || options.signal || options.headers) return request();

  const key = `${timeoutMs}:${path}`;
  if (options.cache === "no-store") cache.recent.delete(key);
  if (cache.inFlight.has(key)) return cache.inFlight.get(key);
  const cached = cache.recent.get(key);
  if (cached && Date.now() - cached.timestamp < DEDUP_CACHE_TTL_MS) return cached.result;
  cache.recent.delete(key);

  const fetchPromise = request();
  cache.inFlight.set(key, fetchPromise);
  try {
    const result = await fetchPromise;
    if (options.cache !== "no-store" && !result.error && result.status >= 200 && result.status < 300) {
      // Bound memory even when owners inspect many different symbols.
      if (cache.recent.size >= 256) cache.recent.delete(cache.recent.keys().next().value);
      cache.recent.set(key, { result, timestamp: Date.now() });
    }
    return result;
  } finally {
    // A rejection is propagated to the caller without spawning a second,
    // unobserved rejecting promise during cache cleanup.
    cache.inFlight.delete(key);
  }
}

function mapConferenceProposalError(result) {
  if (!result || typeof result !== "object") return result;
  if (!result.error && ![0, 401, 403, 404].includes(result.status)) return result;
  const mappedError =
    result.status === 404 ? "Backend-Endpunkt noch nicht verfügbar" :
    result.status === 401 || result.status === 403 ? "Admin-Berechtigung fehlt" :
    result.status === 0 ? "Verbindung zum Backend fehlgeschlagen" :
    result.error;
  return { ...result, error: mappedError };
}

// Step 29: extended error mapper for approve/reject gate actions
function mapApprovalGateError(result) {
  if (!result || typeof result !== "object") return result;
  if (!result.error && ![0, 401, 403, 404, 409, 422, 500].includes(result.status)) return result;
  const mappedError =
    result.status === 404 ? "Backend-Endpunkt noch nicht verfügbar" :
    result.status === 401 || result.status === 403 ? "Keine Berechtigung – bitte Admin-Zugang prüfen" :
    result.status === 409 ? "Freigabe nicht möglich – Vorschlag befindet sich in einem ungültigen Status" :
    result.status === 422 ? "Freigabe konnte nicht verarbeitet werden – Vorschlag-Daten ungültig" :
    result.status === 500 ? "Backend-Fehler – bitte später erneut versuchen" :
    result.status === 0 ? "Verbindung zum Backend fehlgeschlagen" :
    result.error;
  return { ...result, error: mappedError };
}

function mapConferenceCaseLifecycleError(result) {
  if (!result || typeof result !== "object") {
    return { data: null, error: "Unbekannte Backend-Antwort", status: 0, meta: {} };
  }
  // Successful responses keep the uniform apiFetch/adminFetch shape unchanged.
  if (!result.error && ![0, 401, 403, 404].includes(result.status)) return result;
  const mappedError =
    result.status === 404 ? "Case-Lifecycle ist im Backend noch nicht verfügbar" :
    result.status === 401 || result.status === 403 ? "Admin-Berechtigung fehlt" :
    result.status === 0 ? "Verbindung zum Backend fehlgeschlagen" :
    result.error || null;
  return { ...result, error: mappedError };
}

function safeApiText(value, fallback = "") {
  if (typeof value === "string" && value.trim()) return value;
  if (value == null) return fallback;
  if (value instanceof Error && value.message) return value.message;
  if (typeof value === "object") {
    if (typeof value.reason === "string" && value.reason.trim()) return value.reason;
    if (typeof value.message === "string" && value.message.trim()) return value.message;
    if (typeof value.text === "string" && value.text.trim()) return value.text;
  }
  const text = String(value);
  return text && text !== "[object Object]" ? text : fallback;
}

export async function fetchAdminOverview() {
  return adminFetch("/api/admin/overview");
}
export async function fetchAdminPipelineStatus() {
  return adminFetch("/api/admin/pipeline-status");
}
export async function fetchAdminTableHealth() {
  return adminFetch("/api/admin/table-health");
}
export async function fetchAdminRandomCalculated({ n, seed } = {}) {
  const query = buildQuery({ n, seed });
  return adminFetch(`/api/admin-random-calculated${query}`);
}
export async function fetchGuardianStatusSummary() {
  return adminFetch("/api/admin/guardian-status-summary");
}

export async function fetchSystemIntelligence() {
  return adminFetch("/api/admin/system-state");
}
export async function fetchAgentWisdom() {
  return adminFetch("/api/admin/agent-wisdom");
}
export async function fetchAgentWeights() {
  return adminFetch("/api/admin/agent-weights");
}
export async function fetchDynamicWeights() {
  return adminFetch("/api/admin/dynamic-weights");
}
export async function fetchWorldState() {
  return adminFetch("/api/admin/world-state");
}
export async function fetchOperationalStatus() {
  return adminFetch("/api/admin/operational-status");
}
export async function fetchInterfaceState() {
  return adminFetch("/api/admin/interface-state");
}
export async function fetchVirtualPositions() {
  return adminFetch("/api/admin/virtual-positions");
}
export async function fetchNearMisses() {
  return adminFetch("/api/admin/near-misses");
}
export async function fetchSavedCapital() {
  return adminFetch("/api/admin/saved-capital");
}
export async function fetchPortfolioTwin() {
  return adminFetch("/api/admin/portfolio-twin");
}
export async function fetchSignalHistory() {
  return adminFetch("/api/admin/signal-history");
}
export async function fetchOutcomeAnalysis() {
  return adminFetch("/api/admin/outcome-analysis");
}
export async function fetchTimingQuality() {
  return adminFetch("/api/admin/timing-quality");
}
export async function fetchForecastVsOutcome() {
  return adminFetch("/api/admin/forecast-vs-outcome");
}
export async function fetchSignalKpis() {
  return adminFetch("/api/admin/signal-kpis");
}
export async function fetchLearningDiagnostics() {
  return adminFetch("/api/admin/learning-diagnostics");
}

/**
 * GET /api/admin/discovery-results – realisierte Discovery-Ergebnisse
 * (7D/30D returnPct + ausgewertete Stichproben) aus discovery_history.
 * Rein lesend; Horizonte nur, wenn das Backend sie als ausgewertet meldet.
 */
export async function fetchDiscoveryResults() {
  return adminFetch("/api/admin/discovery-results");
}

/**
 * GET /api/admin/factor-forward-returns – persistierte Faktor-Forward-Returns
 * (1H/1D/3D) aus factor_history. Rein lesend; kein 14D, kein erfundener
 * Horizont.
 */
export async function fetchFactorForwardReturns() {
  return adminFetch("/api/admin/factor-forward-returns");
}

export async function fetchSignalHistorySymbol(symbol) {
  return adminFetch(`/api/admin/signal-history/${encodeURIComponent(symbol)}`);
}

// ── Backend-100 Control-Board Endpoints ────────────────────────────────
// These endpoints require X-Admin-Key, which is forwarded server-side by the
// admin proxy (api/admin-proxy.js). The key is never exposed in client code.

/** Operator summary – main status + top issues + next actions. */
export async function fetchOperatorSummary() {
  return adminFetch("/api/admin/operator-summary", HEAVY_ADMIN_TIMEOUT_MS);
}

/** Quality audit – readiness, confidence, data coverage, weight maturity. */
export async function fetchQualityAudit() {
  return adminFetch("/api/admin/quality-audit");
}

/** Forecast quality – forecast counts, learning state, quality status. */
export async function fetchForecastQuality() {
  return adminFetch("/api/admin/forecast-quality");
}

/**
 * Frontend Critical Summary – read-only dashboard status for frontend.
 * Public endpoint (no admin auth required on frontend side).
 * Backend aggregates critical health metrics for dashboard display.
 * 
 * Returns: {
 *   success: boolean,
 *   healthy: boolean,
 *   data: {
 *     status: string,           // "operational" | "degraded" | "offline"
 *     partial: boolean,         // true if some data sources unavailable
 *     stale: boolean,           // true if data is outdated
 *     errors: string[],         // list of current error messages
 *     lastUpdated: string,      // ISO timestamp of last backend update
 *     metrics?: {               // optional detailed metrics
 *       database: boolean,
 *       apis: boolean,
 *       services: boolean,
 *     }
 *   }
 * }
 */
export async function fetchFrontendCriticalSummary() {
  // Owner-only endpoint: forwards the caller's own Supabase Bearer token
  // via the shared authenticatedFetch() helper (fails closed with a 401
  // if no session is known, never fires anonymously).
  return authenticatedFetch("/api/frontend-critical-summary", {}, DEFAULT_TIMEOUT_MS);
}

/**
 * Frontend Critical Summary Readiness – lightweight health check.
 * Returns minimal status for quick readiness probe.
 * 
 * Returns: {
 *   success: boolean,
 *   ready: boolean,
 *   timestamp: string
 * }
 */
export async function fetchFrontendCriticalSummaryReadiness() {
  // Owner-only endpoint: same Bearer-passthrough auth strategy as
  // fetchFrontendCriticalSummary() above.
  return authenticatedFetch("/api/frontend-critical-summary/readiness", {}, 5_000);
}

// ── Backend-100 Drilldown Endpoints (optional) ─────────────────────────

/** Service diagnostics – per-service health details. */
export async function fetchServiceDiagnostics() {
  return adminFetch("/api/admin/service-diagnostics", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** Data-flow health – pipeline and data-flow status. */
export async function fetchDataFlowHealth() {
  return adminFetch("/api/admin/data-flow-health", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** Database capacity – DB storage and capacity metrics. */
export async function fetchDbCapacity() {
  return adminFetch("/api/admin/db-capacity", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * Masterdata Shadow Report – compares SEC/Nasdaq values against existing
 * provider values without writing anything.
 *
 * @param {{ limit?: number, fields?: string, symbols?: string, includeDetails?: boolean }} [params]
 */
export async function fetchMasterDataShadowReport({ limit = 500, fields, symbols, includeDetails = false } = {}) {
  const query = buildQuery({ limit, fields, symbols, includeDetails });
  return adminFetch(`/api/admin/data-quality/masterdata-shadow${query}`);
}

/**
 * Masterdata Promotion Plan – dry-run read of which fields would be ready
 * for an official-first promotion. Read-only; no data is written.
 *
 * @param {{ limit?: number, fields?: string, includeCandidates?: boolean }} [params]
 */
export async function fetchMasterDataPromotionPlan({ limit = 500, fields, includeCandidates = false } = {}) {
  const query = buildQuery({ limit, fields, includeCandidates });
  return adminFetch(`/api/admin/data-quality/masterdata-promotion-plan${query}`);
}

/**
 * Data Freshness – monitoring/stale-detection summary.
 * GET /api/admin/data-freshness
 * Returns { success, data: { status, score, criticalCount, warningCount, unknownCount, checks[], areas } }
 */
export async function fetchDataFreshness() {
  return adminFetch("/api/admin/data-freshness", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * Core Readiness – overall system readiness for frontend, proof and autonomous conference.
 * GET /api/admin/core-readiness?details=true&window=30d&limit=50
 *
 * @param {{ details?: boolean, window?: string, limit?: number }} [options]
 */
export async function fetchCoreReadiness(options = {}) {
  const query = buildQuery({
    details: options.details ?? true,
    window: options.window,
    limit: options.limit,
  });
  return adminFetch(`/api/admin/core-readiness${query}`, MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * Performance Proof – verified outcomes, win rate, PnL metrics.
 * GET /api/admin/performance-proof?window=30d&limit=50
 *
 * @param {{ window?: string, limit?: number }} [options]
 */
export async function fetchPerformanceProof(options = {}) {
  const query = buildQuery({
    window: options.window,
    limit: options.limit,
  });
  return adminFetch(`/api/admin/performance-proof${query}`, MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * SEC EDGAR Coverage – row counts, symbol coverage, tag coverage.
 * GET /api/sec-edgar/coverage
 *
 * @param {object} [options]
 */
export async function fetchSecEdgarCoverage(options = {}) {
  const query = buildQuery(options);
  return adminFetch(`/api/sec-edgar/coverage${query}`, MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * Provider / Fallback Health – configured providers, fallback order, health flags.
 * GET /api/admin/data-quality/provider-fallback-health
 * Returns 404 when the endpoint is not yet deployed; callers must handle gracefully.
 *
 * @param {object} [options]
 */
export async function fetchProviderFallbackHealth(options = {}) {
  const query = buildQuery(options);
  return adminFetch(`/api/admin/data-quality/provider-fallback-health${query}`, MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * Universe Quality Gate summary – optional admin cockpit source.
 * Returns 404 as a non-fatal unavailable response when not deployed yet.
 */
export async function fetchUniverseQualityGateSummary(options = {}) {
  const query = buildQuery(options);
  return adminFetch(`/api/admin/universe-quality-gate/summary${query}`, MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * Data Healing Plan summary – optional Step 13.3/13.4 cockpit source.
 */
export async function fetchDataHealingPlanSummary(options = {}) {
  const query = buildQuery(options);
  return adminFetch(`/api/admin/data-healing-plan/summary${query}`, MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * Data Healing Plan top actions – limited, UI-friendly action list.
 */
export async function fetchDataHealingPlanTop(options = {}) {
  const query = buildQuery({ limit: options.limit ?? 10, ...options });
  return adminFetch(`/api/admin/data-healing-plan/top${query}`, MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * Data Healing Run preview – read-only preview for a single healing action.
 */
export async function fetchDataHealingRunPreview(action, options = {}) {
  const token = await getAccessToken();
  return apiFetch("/api/admin/data-healing-run/preview", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action, options }),
  }, MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * Learning Loop summary – optional frontend endpoint when available.
 */
export async function fetchLearningLoopSummary(options = {}) {
  const query = buildQuery(options);
  return adminFetch(`/api/admin/learning-loop-summary${query}`, MEDIUM_ADMIN_TIMEOUT_MS);
}

// ── Weight Learning Recommendations (Step 13 Frontend / Step 12 Backend) ──────

/**
 * Readiness summary for the weight-learning recommendation ledger.
 * GET /api/admin/weight-learning-recommendations/readiness
 * Returns { success, data: { total, proposed, shadow, evaluated, ... } }
 */
export async function fetchWeightLearningRecommendationReadiness() {
  return adminFetch("/api/admin/weight-learning-recommendations/readiness", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * List weight-learning recommendations with optional filters.
 * GET /api/admin/weight-learning-recommendations?status=shadow&limit=5
 *
 * @param {{ status?: string, limit?: number }} [params]
 */
export async function fetchWeightLearningRecommendations(params = {}) {
  const query = buildQuery({ status: params.status, limit: params.limit });
  return adminFetch(`/api/admin/weight-learning-recommendations${query}`, MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * Create a new weight-learning recommendation (simulation trigger).
 * POST /api/admin/weight-learning-recommendations
 *
 * @param {{ window?: string, shadow?: boolean }} [payload]
 */
export async function createWeightLearningRecommendation(payload = {}) {
  const token = await getAccessToken();
  return apiFetch("/api/admin/weight-learning-recommendations", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * Trigger evaluate-shadow for a specific recommendation.
 * POST /api/admin/weight-learning-recommendations/:id/evaluate-shadow
 *
 * @param {string|number} id
 */
export async function evaluateWeightLearningRecommendation(id) {
  const token = await getAccessToken();
  return apiFetch(`/api/admin/weight-learning-recommendations/${encodeURIComponent(id)}/evaluate-shadow`, {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  }, MEDIUM_ADMIN_TIMEOUT_MS);
}

/** Fetch tech radar items with optional filters. */
export async function fetchTechRadarAdmin(filters = {}) {
  const query = buildQuery({
    limit: filters.limit ?? 100,
    category: filters.category,
    status: filters.status,
    fitForHQS: filters.fitForHQS,
    hasLink: filters.hasLink,
    isNew: filters.isNew,
    unreviewed: filters.unreviewed,
    adoptionTiming: filters.adoptionTiming,
    relevance: filters.relevance,
    strategicFit: filters.strategicFit,
    timeHorizon: filters.timeHorizon,
    decisionHint: filters.decisionHint,
    stackFit: filters.stackFit,
  });
  return adminFetch(`/api/admin/tech-radar-admin${query}`);
}

/** Update status of a single tech radar item. */
export async function updateTechRadarAdminStatus(id, payload) {
  const token = await getAccessToken();
  return apiFetch(`/api/admin/tech-radar-admin/${encodeURIComponent(id)}/status`, {
    method: "PATCH",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

// ── Admin DeepSeek Console ─────────────────────────────────────────────

/**
 * Send a request to the DeepSeek admin console endpoint.
 *
 * @param {{ message: string, mode: string, context?: string, logs?: string[], changedFiles?: string[], notes?: string }} payload
 */
export async function postAdminDeepseekChat(payload) {
  const token = await getAccessToken();
  return apiFetch("/api/admin/deepseek/chat", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, AGENT_TIMEOUT_MS);
}

// ── Admin DeepSeek Math & Logic Review ──────────────────────────────────

/**
 * Send a Math & Logic Review request to the DeepSeek admin endpoint.
 *
 * @param {{ message: string, context?: string, logs?: string[], changedFiles?: string[], notes?: string, focusAreas?: string[] }} payload
 */
export async function postAdminMathLogicReview(payload) {
  const token = await getAccessToken();
  return apiFetch("/api/admin/deepseek/math-logic-review", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, AGENT_TIMEOUT_MS);
}

// ── Admin DeepSeek Controller Guard ─────────────────────────────────────

/**
 * Run a Controller-Guard V1 check against the DeepSeek admin endpoint.
 *
 * @param {{ context?: string, logs?: string[], changedFiles?: string[], notes?: string }} payload
 */
export async function postAdminControllerGuard(payload) {
  const token = await getAccessToken();
  return apiFetch("/api/admin/deepseek/controller-guard", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, AGENT_TIMEOUT_MS);
}

// ── Admin DeepSeek Agent (thread-based) ─────────────────────────────────

/**
 * Send a message to the DeepSeek agent chat (server-side conversation store).
 *
 * @param {object} payload  { message, mode?, conversationId?, context?, changedFiles?, logs?, notes? }
 * @returns Promise with data: { success, conversationId, mode, model, reply: { text }, reasoningContent? }
 */
export async function postAdminDeepseekAgentChat(payload) {
  const token = await getAccessToken();
  return apiFetch("/api/admin/deepseek/agent-chat", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, AGENT_TIMEOUT_MS);
}

/**
 * Propose a change via the DeepSeek agent.
 *
 * @param {object} payload  { conversationId, message?, targetFiles?, changeDescription? }
 * @returns Promise with data: { success, conversationId, proposal: { description, affectedFiles, risk, riskNote, editPlan } }
 */
export async function postAdminDeepseekProposeChange(payload) {
  const token = await getAccessToken();
  return apiFetch("/api/admin/deepseek/propose-change", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, AGENT_TIMEOUT_MS);
}

/**
 * Prepare a patch from a DeepSeek proposal.
 *
 * @param {object} payload  { conversationId, proposal, dryRun? }
 * @returns Promise with data: { success, conversationId, patch: { files, diffPreview, dryRun, warnings } }
 */
export async function postAdminDeepseekPreparePatch(payload) {
  const token = await getAccessToken();
  return apiFetch("/api/admin/deepseek/prepare-patch", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, AGENT_TIMEOUT_MS);
}

/**
 * Execute a prepared change via the DeepSeek agent.
 *
 * @param {object} payload  { conversationId, patch, confirmation }
 * @returns Promise with data: { success, conversationId, execution: { status, changedFiles, summary, warnings } }
 */
export async function postAdminDeepseekExecuteChange(payload) {
  const token = await getAccessToken();
  return apiFetch("/api/admin/deepseek/execute-change", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, AGENT_TIMEOUT_MS);
}

// ── Admin DeepSeek Change Memory ────────────────────────────────────────

/** Fetch change-memory entries with optional filters. */
export async function getAdminChangeMemory(params = {}) {
  const query = buildQuery({
    status: params.status,
    riskLevel: params.riskLevel,
    wasHelpful: params.wasHelpful,
    limit: params.limit,
  });
  return adminFetch(`/api/admin/deepseek/change-memory${query}`);
}

/** Fetch a single change-memory entry by id. */
export async function getAdminChangeMemoryById(id) {
  return adminFetch(`/api/admin/deepseek/change-memory/${encodeURIComponent(id)}`);
}

/** Update fields of a change-memory entry. */
export async function patchAdminChangeMemory(id, payload) {
  const token = await getAccessToken();
  return apiFetch(`/api/admin/deepseek/change-memory/${encodeURIComponent(id)}`, {
    method: "PATCH",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

// ── Data-source probe ──────────────────────────────────────────────────

export async function fetchDataSources(probe = false) {
  return apiFetch(`/api/data-sources${probe ? "?probe=1" : ""}`);
}

// ── Admin Gemini Architect ─────────────────────────────────────────────

/**
 * Send a frontend-architect review request to the Gemini backend.
 *
 * @param {object} payload  e.g. { mode, message, context, affectedViews, affectedComponents, notes }
 */
export async function postAdminGeminiArchitectReview(payload) {
  const token = await getAccessToken();
  return apiFetch("/api/admin/gemini/architect-review", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, AGENT_TIMEOUT_MS);
}

/**
 * Send a message to the Gemini agent chat (with conversation context).
 *
 * @param {object} payload  { message, mode?, conversationId?, context?, affectedViews?, affectedComponents?, notes? }
 * @returns Promise with data: { success, conversationId, mode, model, reply: { text } }
 */
export async function postAdminGeminiAgentChat(payload) {
  const token = await getAccessToken();
  return apiFetch("/api/admin/gemini/agent-chat", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, AGENT_TIMEOUT_MS);
}

/**
 * Propose a change via the Gemini agent.
 *
 * @param {object} payload  { conversationId, message?, targetFiles?, changeDescription? }
 * @returns Promise with data: { success, conversationId, proposal: { description, affectedFiles, risk, riskNote, editPlan } }
 */
export async function postAdminGeminiProposeChange(payload) {
  const token = await getAccessToken();
  return apiFetch("/api/admin/gemini/propose-change", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, AGENT_TIMEOUT_MS);
}

/**
 * Prepare a patch from a Gemini proposal.
 *
 * @param {object} payload  { conversationId, proposal, dryRun? }
 * @returns Promise with data: { success, conversationId, patch: { files, diffPreview, dryRun, warnings } }
 */
export async function postAdminGeminiPreparePatch(payload) {
  const token = await getAccessToken();
  return apiFetch("/api/admin/gemini/prepare-patch", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, AGENT_TIMEOUT_MS);
}

/**
 * Execute a prepared change via the Gemini agent.
 *
 * @param {object} payload  { conversationId, patch, confirmation }
 * @returns Promise with data: { success, conversationId, execution: { status, changedFiles, summary, warnings } }
 */
export async function postAdminGeminiExecuteChange(payload) {
  const token = await getAccessToken();
  return apiFetch("/api/admin/gemini/execute-change", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, AGENT_TIMEOUT_MS);
}

// ── Admin v0 Design Agent ──────────────────────────────────────────────

/**
 * Send a design request to the v0 design agent.
 *
 * @param {object} payload  { message, conversationId?, context?, currentView?, currentFile? }
 * @returns Promise with data: { success, conversationId, model, reply: { text, design?: { summary, recommendations[], affectedFiles[], warnings[], nextStep } } }
 */
export async function postAdminV0AgentChat(payload) {
  const token = await getAccessToken();
  return apiFetch("/api/admin/v0/agent-chat", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, AGENT_TIMEOUT_MS);
}

// ── Admin Agent Bridge ─────────────────────────────────────────────────

/**
 * Retrieve the current agent bridge context (DeepSeek ↔ Gemini).
 * Returns { backendState, bridgeHints }.
 */
export async function getAdminAgentBridge() {
  const token = await getAccessToken();
  return apiFetch("/api/admin/deepseek/agent-bridge", {
    method: "GET",
    _accessToken: token,
    _sameOrigin: true,
  });
}

/**
 * Send bridge hints through the agent bridge.
 *
 * @param {object} payload  e.g. { hints: [...] }
 */
export async function postAdminAgentBridgeHints(payload) {
  const token = await getAccessToken();
  return apiFetch("/api/admin/deepseek/agent-bridge/hints", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/**
 * Send structured frontend feedback back through the bridge.
 *
 * @param {object} payload  e.g. { view, observation, suspectedCause, affectedComponents, recommendedBackendCheck }
 */
export async function postAdminAgentBridgeFrontendFeedback(payload) {
  const token = await getAccessToken();
  return apiFetch("/api/admin/deepseek/agent-bridge/frontend-feedback", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/**
 * Fetch the pattern-memory summary from the agent bridge.
 * Returns recognised patterns, frequent layers, follow-up types and confidence bands.
 */
export async function getAdminPatternMemorySummary() {
  return adminFetch("/api/admin/deepseek/agent-bridge/pattern-memory");
}

// ── Trust / Outcome / Learning Quality ─────────────────────────────────

/**
 * Lightweight trust overview for the dashboard.
 * Returns topStrongPatterns, frequentButWeakPatterns, earlySignals,
 * helpfulFollowups, usefulModes.
 */
export async function getAdminTrustOverview() {
  return adminFetch("/api/admin/deepseek/agent-bridge/trust-overview");
}

/**
 * Full trust summary with all patterns sorted by band and usefulness.
 */
export async function getAdminTrustSummary() {
  return adminFetch("/api/admin/deepseek/agent-bridge/trust-summary");
}

/**
 * Trust data for a single pattern by key.
 */
export async function getAdminPatternTrust(patternKey) {
  return adminFetch(`/api/admin/deepseek/agent-bridge/pattern-trust/${encodeURIComponent(patternKey)}`);
}

/**
 * Record an outcome signal for a pattern.
 * @param {object} payload  { patternKey, outcomeSignal, followupCategory?, recommendedMode?, layer?, notes? }
 */
export async function postAdminOutcomeSignal(payload) {
  const token = await getAccessToken();
  return apiFetch("/api/admin/deepseek/agent-bridge/outcome-signal", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/**
 * Trust diagnostics health check (pattern count, follow-up count, etc.).
 */
export async function getAdminTrustDiagnostics() {
  return adminFetch("/api/admin/deepseek/agent-bridge/trust-diagnostics");
}

// ── Action Readiness / Recommendation Quality ─────────────────────────────

/**
 * Action readiness and recommendation quality overview.
 * Returns readiness bands, recommendation quality, next-step suggestions,
 * and per-signal actionability assessments.
 */
export async function getAdminActionReadiness() {
  return adminFetch("/api/admin/deepseek/agent-bridge/action-readiness");
}

// ── Recommendation Review / Improvement ────────────────────────────────

/**
 * Recommendation review and improvement overview.
 * Returns recommendation feedback bands, improvement signals,
 * outcome categories, and system-level improvement summaries.
 */
export async function getAdminRecommendationReview() {
  return adminFetch("/api/admin/deepseek/agent-bridge/recommendation-improvement");
}

// ── Governance / Policy ────────────────────────────────────────────────

/**
 * Governance and policy overview for the current recommendation set.
 * Returns policyClass, visibilityPolicy, governanceBand, promotionReadiness,
 * surfaceEligibility, needsMoreEvidence, guardianEligibility, and optional
 * summary counts / per-item governance classifications.
 */
export async function getAdminGovernancePolicy() {
  return adminFetch("/api/admin/deepseek/agent-bridge/governance-policy");
}

// ── Surface Routing / Controlled Promotion ─────────────────────────────

/**
 * Surface-routing summary — returns per-class counts (shadow_only,
 * internal_admin, guardian_watch, guardian_candidate, companion_later),
 * grouped pattern lists, guardianDistinction, and companionSummary.
 */
export async function getAdminSurfaceRouting() {
  return adminFetch("/api/admin/deepseek/agent-bridge/surface-routing");
}

// ── Issue Intelligence / Error Detection ───────────────────────────────

/**
 * Issue intelligence and error detection overview.
 * Returns detected issues with issueType, issueCategory, issueSeverity,
 * affectedLayer, suspectedCause, suggestedFix, needsFollowup,
 * issueConfidence, and optional summary / category counts.
 */
export async function getAdminIssueIntelligence() {
  return adminFetch("/api/admin/deepseek/agent-bridge/issue-intelligence");
}

// ── Case / Resolution / Operator Loop ──────────────────────────────────

/**
 * Case/Resolution summary — returns per-status counts (open, watching,
 * confirmed, resolved, dismissed, needs_followup), recent case list with
 * caseStatus, caseOutcome, helpfulnessBand, caseReason, followupNeeded,
 * followupStatus, wasHelpful, and optional summary / helpfulness distribution.
 */
export async function getAdminCaseResolutionSummary() {
  return adminFetch("/api/admin/deepseek/agent-bridge/case-resolution");
}

// ── Attention / Priority / Operator Focus ──────────────────────────────

/**
 * Attention/Priority summary — returns attention-band distribution
 * (focus_now, review_today, watch_next, background), per-entry attention
 * data with attentionBand, priorityBand, operatorFocus, attentionReason,
 * priorityReason, focusDrivers, whyNow, attentionScore, and optional
 * summary / driver aggregation.
 */
export async function getAdminAttentionPrioritySummary() {
  return adminFetch("/api/admin/deepseek/agent-bridge/attention-priority");
}

// ── Resolution Confidence / Decision Maturity ──────────────────────────

/**
 * Decision-Maturity summary — returns maturity band distribution
 * (early_signal, building, credible, confirmed), per-entry maturity data
 * with decisionMaturityBand, maturityScore, maturityReason, maturityDrivers,
 * maturityContext, and optional bandCounts / divergence list.
 *
 * If the dedicated endpoint is not yet available the frontend falls back to
 * deriving maturity from the trust overview.
 */
export async function getAdminDecisionMaturitySummary() {
  return adminFetch("/api/admin/deepseek/agent-bridge/decision-maturity");
}

// ── Agent Chat / Cases / Messages / Approval ───────────────────────────

/**
 * Agent-Cases summary — returns active agent cases with problem,
 * cause, solution, approval status, agent role, and thread messages.
 *
 * If the dedicated endpoint is not yet available the frontend falls
 * back to deriving lightweight cases from existing bridge data.
 */
export async function getAdminAgentCases() {
  return adminFetch("/api/admin/deepseek/agent-bridge/agent-cases");
}

/**
 * Agent-Messages for a specific case thread — returns chat-style
 * messages from DeepSeek, Gemini, and user responses.
 */
export async function getAdminAgentMessages(caseId) {
  return adminFetch(`/api/admin/deepseek/agent-bridge/agent-messages/${encodeURIComponent(caseId)}`);
}

/**
 * Submit an approval decision or feedback response for an agent case.
 * Payload: { caseId, decision, comment, scope }
 */
export async function postAdminAgentApproval(payload) {
  return adminFetch("/api/admin/deepseek/agent-bridge/agent-approval", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/**
 * Send a user chat message into an agent case thread.
 * Payload: { caseId, message, intent, targetAgent }
 *
 * Timeout uses AGENT_TIMEOUT_MS because the backend calls DeepSeek and
 * Gemini in parallel and may take up to 60 s on complex prompts.
 */
export async function postAdminAgentChatMessage(payload) {
  const token = await getAccessToken();
  return apiFetch(
    "/api/admin/deepseek/agent-bridge/agent-chat",
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    AGENT_TIMEOUT_MS,
  );
}

/**
 * Plan-refinement summary — returns counts of cases with refined plans,
 * ready for preparation, needing decisions, diagnosis-only, cross-agent review, etc.
 */
export async function getAdminPlanRefinementSummary() {
  return adminFetch("/api/admin/deepseek/agent-bridge/plan-refinement-summary");
}

// ── Controlled Action Drafts / Fix Bundles ─────────────────────────────

/**
 * Action-Draft / Fix-Bundle summary — returns prepared solution drafts
 * with draftType, draftStatus, changeCategory, preparationOwner,
 * affectedTargets, bundleSteps, handoffSuggested, needsCrossAgentReview,
 * and optional per-case draft attachments.
 */
export async function getAdminActionDraftSummary() {
  return adminFetch("/api/admin/deepseek/agent-bridge/action-draft-summary");
}

// ── Apply-Readiness / Final Approval ─────────────────────────────────

/**
 * Apply-Readiness / Final Approval summary — returns readiness bands,
 * blocked cases, eligible-for-apply counts, open checks, risk flags,
 * and execution proposals for agent cases ready for final approval.
 */
export async function getAdminApplyReadinessSummary() {
  return adminFetch("/api/admin/deepseek/agent-bridge/apply-readiness-summary");
}

// ── Execution Preview / Safety / Rollback (Step 18) ──────────────────

/**
 * Execution Preview / Apply Simulation / Safety / Rollback summary —
 * returns preview states, safety bands, expected impact counts,
 * rollback complexity distribution, apply window recommendations,
 * cross-agent safety review requirements, and per-case preview data.
 */
export async function getAdminExecutionPreviewSummary() {
  return adminFetch("/api/admin/deepseek/agent-bridge/execution-preview-summary");
}

// ── Apply Candidate / Action Package / Approval Gate (Step 19) ────────

/**
 * Apply Candidate / Action Package / Approval Gate summary —
 * returns candidate statuses, candidate modes, included/excluded actions,
 * allowed/disallowed targets, guardrails, approval checklist state,
 * final preconditions, missing approvals, and candidate ownership.
 */
export async function getAdminApplyCandidateSummary() {
  return adminFetch("/api/admin/deepseek/agent-bridge/apply-candidate-summary");
}

// ── Execution Runtime / Start Approval / Abort & Kill Switch (Step 20) ─

/**
 * Controlled Execution Runtime / Start Approval / Audit summary —
 * returns runtime statuses, execution states, runtime modes,
 * abort / kill-switch / rollback availability, runtime guardrails,
 * runtime ownership, block reasons, and per-case runtime session data.
 */
export async function getAdminExecutionRuntimeSummary() {
  return adminFetch("/api/admin/deepseek/agent-bridge/execution-runtime-summary");
}

// ── Persistent Case Chat / Agent Conversation Runtime (Step 21) ─────────

/**
 * Conversation summary — returns open thread counts, threads awaiting user,
 * threads awaiting agent, deepseek-dominant / gemini-dominant / cross-agent
 * thread counts, and which cases are currently in active dialogue.
 */
export async function getAdminConversationSummary() {
  return adminFetch("/api/admin/deepseek/agent-bridge/conversation-summary");
}

/**
 * Persistent case chat thread — returns messages with role, text, timestamp,
 * messageIntent, replyToMessageId, nextSuggestedSpeaker, conversationState,
 * awaitingUserReply, awaitingAgentReply for a specific case.
 */
export async function getAdminCaseChatThread(caseId) {
  return adminFetch(
    `/api/admin/deepseek/agent-bridge/case-chat-thread/${encodeURIComponent(caseId)}`,
  );
}

// ── Cross-Agent Handoff / Coordinated Dialogue (Step 22) ────────────────

/**
 * Cross-Agent Handoff / Coordinated Dialogue summary —
 * returns handoff status distribution (pending, active, completed),
 * cross-agent state (single_agent, handoff_proposed, handoff_active,
 * cross_agent_active, handoff_completed), dominant/supporting agent
 * assignment per case, handoff-from/to, handoff reasons,
 * handoff history entries, and a cross-agent summary text.
 */
export async function getAdminCrossAgentHandoffSummary() {
  return adminFetch("/api/admin/deepseek/agent-bridge/handoff-summary");
}

// ── Case Memory / Working Summary / Continuity (Step 23) ────────────────

/**
 * Case Memory / Working Summary / Continuity summary —
 * returns per-case workingSummary, agreedDirection, openQuestions,
 * resolvedPoints, nextOpenStep, memoryAnchors, caseContinuityStatus,
 * memoryFreshness, lastContributingAgent, memoryFocus, caseScope,
 * discardedDirections, and aggregate continuity counts.
 */
export async function getAdminCaseMemorySummary() {
  return adminFetch("/api/admin/deepseek/agent-bridge/case-memory-summary");
}

// ── Action Negotiation / Option Comparison / Decision Framing (Step 24) ─

/**
 * Decision Framing summary — returns per-case actionOptions (2–4),
 * recommendedOptionId, conservativeOptionId, fastestOptionId,
 * widestOptionId, safestOptionId, decisionTradeoff, nextDecisionQuestion,
 * decisionFrameStatus, decisionMode, preferredStrategy,
 * primaryDecisionAgent, secondaryDecisionAgent, decisionFocus,
 * narrowingHint, userDecisionNeeded, and aggregate decision-frame counts.
 */
export async function getAdminDecisionFramingSummary() {
  return adminFetch("/api/admin/deepseek/agent-bridge/decision-frame-summary");
}

// ── OpenAI Operations Layer (Step 25) ──────────────────────────────────

/**
 * Fetch the OpenAI Operations Layer decision summary.
 * Canonical shape: summary, rootCause, priority, riskLevel,
 * recommendedNextStep, needsHumanApproval, decisionType, confidence,
 * agentAssignments[]: { agent, role, scope, task, reason },
 * proposedActions[]: { label, type, target, requiresApproval, priority },
 * evidence: { sources[], filesRead[], keySignals[] },
 * operationsModelUsed, operationsInputSources,
 * operationsConfidence, operationsDecisionType.
 *
 * Uses POST because the backend endpoint triggers an LLM synthesis pass
 * (like all other AI operations endpoints) and the Railway backend only
 * registers a POST handler for this path.
 */
export async function getAdminOpenAIOperationsSummary() {
  const token = await getAccessToken();
  return apiFetch("/api/admin/openai/operations-summary", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  }, AGENT_TIMEOUT_MS);
}

// ── Admin Agent Task Progress ───────────────────────────────────────────

/**
 * Fetch the current task-progress / run status for a given agent conversation.
 * Returns { status, currentStep, progressPct, completedSteps, nextPlannedStep,
 *           activeFile, activeTool, lastAgentAction, resumable }.
 *
 * Falls back gracefully (returns data: null) if the backend has not yet
 * implemented the task-progress endpoint (HTTP 501 / 404).
 *
 * @param {"deepseek"|"gemini"|"openai"|"v0"|"conference"} agent
 * @param {string} conversationId
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function getAdminAgentTaskStatus(agent, conversationId) {
  if (!conversationId) return { data: null, error: null, status: 0, meta: {} };
  const token = await getAccessToken();
  return apiFetch(`/api/admin/agent-task/${encodeURIComponent(agent)}/${encodeURIComponent(conversationId)}/status`, {
    method: "GET",
    _accessToken: token,
    _sameOrigin: true,
  }, DEFAULT_TIMEOUT_MS);
}

/**
 * Fetch the list of task steps for a given agent conversation run.
 *
 * @param {"deepseek"|"gemini"|"openai"|"v0"|"conference"} agent
 * @param {string} conversationId
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function getAdminAgentTaskSteps(agent, conversationId) {
  if (!conversationId) return { data: null, error: null, status: 0, meta: {} };
  const token = await getAccessToken();
  return apiFetch(`/api/admin/agent-task/${encodeURIComponent(agent)}/${encodeURIComponent(conversationId)}/steps`, {
    method: "GET",
    _accessToken: token,
    _sameOrigin: true,
  }, DEFAULT_TIMEOUT_MS);
}

/**
 * Resume a paused / failed agent task run.
 *
 * @param {"deepseek"|"gemini"|"openai"|"v0"|"conference"} agent
 * @param {string} conversationId
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function postAdminAgentTaskResume(agent, conversationId) {
  if (!conversationId) return { data: null, error: null, status: 0, meta: {} };
  const token = await getAccessToken();
  return apiFetch(`/api/admin/agent-task/${encodeURIComponent(agent)}/${encodeURIComponent(conversationId)}/resume`, {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  }, AGENT_TIMEOUT_MS);
}

// ── Admin Claude Agent ─────────────────────────────────────────────────

/**
 * Send a message to the Claude agent chat (with server-side conversation history).
 *
 * @param {object} payload  { message, mode?, conversationId?, context?, targetFiles?, notes? }
 * @returns Promise with data: { success, conversationId, mode, model, reply: { text } }
 */
export async function postAdminClaudeAgentChat(payload) {
  const token = await getAccessToken();
  return apiFetch("/api/admin/claude/agent-chat", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, AGENT_TIMEOUT_MS);
}

/**
 * Propose a change via the Claude agent.
 *
 * @param {object} payload  { conversationId, message?, targetFiles?, changeDescription? }
 * @returns Promise with data: { success, conversationId, proposal: { description, affectedFiles[], risk, riskNote, editPlan[] } }
 */
export async function postAdminClaudeProposeChange(payload) {
  const token = await getAccessToken();
  return apiFetch("/api/admin/claude/propose-change", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, AGENT_TIMEOUT_MS);
}

/**
 * Prepare a patch from a Claude proposal.
 *
 * @param {object} payload  { conversationId, proposal, dryRun? }
 * @returns Promise with data: { success, conversationId, patch: { files, diffPreview, dryRun, warnings } }
 */
export async function postAdminClaudePreparePatch(payload) {
  const token = await getAccessToken();
  return apiFetch("/api/admin/claude/prepare-patch", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, AGENT_TIMEOUT_MS);
}

/**
 * Execute a prepared change via the Claude agent.
 *
 * @param {object} payload  { conversationId, patch, confirmation }
 * @returns Promise with data: { success, conversationId, execution: { status, changedFiles, summary, warnings } }
 */
export async function postAdminClaudeExecuteChange(payload) {
  const token = await getAccessToken();
  return apiFetch("/api/admin/claude/execute-change", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, AGENT_TIMEOUT_MS);
}

// ── Admin bypass auth ──────────────────────────────────────────────────

export async function fetchAdminBypassStatus() {
  return apiFetch("/api/admin-bypass-status");
}

/**
 * Same-origin POST login for the admin bypass. The token is sent exclusively
 * in the JSON request body — never as a URL query parameter — so it cannot
 * leak into browser history, server access logs or Referer headers.
 */
export async function loginAdminBypass(token) {
  return apiFetch("/api/admin-bypass-login", {
    method: "POST",
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
}

export async function logoutAdminBypass() {
  return apiFetch("/api/admin-bypass-logout", {
    method: "POST",
    _sameOrigin: true,
  });
}

// ── Orchestrator Endpoint ─────────────────────────────────────────────
// Single adaptive flow endpoint — the backend decides which agents run and
// in which order.  The frontend only needs to send { message, mode }.

/**
 * Run the orchestrator's adaptive multi-agent flow.
 *
 * @param {string} message - User prompt
 * @param {string} [mode="adaptive"] - Flow mode (currently only "adaptive" is supported)
 * @returns {Promise<{ data: { flow: Array<{ agent: string, step: number, status: "success"|"error", content: string|null, error: string|null }> } | null, error: string|null, status: number, meta: object }>}
 */
export async function postOrchestratorRun(message, mode = "adaptive") {
  return apiFetch(
    "/api/orchestrator/run",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message, mode }),
    },
    AGENT_TIMEOUT_MS,
  );
}

/**
 * Submit user feedback for a completed orchestrator flow.
 *
 * @param {{ rating: "good"|"bad", score?: number, stepCount?: number, successCount?: number }} payload
 * @returns {Promise<{ data: { success: boolean }|null, error: string|null, status: number, meta: object }>}
 */
export async function postOrchestratorFeedback(payload) {
  return apiFetch(
    "/api/orchestrator/feedback",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

// ── Direct Agent & Conference Endpoints ───────────────────────────────
// These use the BFF's own agent services (not the Railway proxy) so they
// work even when the Railway backend is unreachable.

/**
 * Send a prompt directly to a named agent.
 *
 * @param {"deepseek"|"gemini"|"claude"|"openai"} agentName
 * @param {string} prompt
 * @returns {Promise<{ data: { agent, status, data, error } | null, error: string|null, status: number, meta: object }>}
 */
export async function postDirectAgentChat(agentName, prompt) {
  return apiFetch(
    `/api/agent/${encodeURIComponent(agentName)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ prompt }),
    },
    AGENT_TIMEOUT_MS,
  );
}

/** Default set of agents queried in a full direct conference. */
const DEFAULT_CONFERENCE_AGENTS = ["gemini", "claude", "openai", "deepseek", "qwen", "v0"];

/**
 * Run a conference via the agent-bridge endpoint.
 *
 * POST /api/admin/deepseek/agent-bridge/conference-multi-agent-message
 *
 * @param {string} message
 * @param {string[]} [agents] - Agent identifiers to query (defaults to all conference agents)
 * @returns {Promise<{ data: { messages: Array<{ sender: string, content: string, status: string, error: string|null, errorCategory: string|null }> } | null, error: string|null, status: number, meta: object }>}
 */
export async function postDirectConference(
  message,
  agents = DEFAULT_CONFERENCE_AGENTS,
) {
  const payload = { message, selectedAgents: normalizeConferenceAgentIds(agents) };
  if (import.meta.env.DEV) {
    console.log(
      "REQUEST →",
      "/api/admin/deepseek/agent-bridge/conference-multi-agent-message",
      payload,
    );
  }

  return apiFetch(
    "/api/admin/deepseek/agent-bridge/conference-multi-agent-message",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    AGENT_TIMEOUT_MS,
  );
}

// ── Multi-Agent Conference (new backend routing) ──────────────────────────

/**
 * Canonical agent ID map.
 * Maps display names and aliases to the canonical IDs the backend expects.
 * Qwen and v0 are conference participants; secrets stay backend-only.
 */
const AGENT_NAME_TO_CANONICAL = {
  deepseek: "deepseek",
  gemini: "gemini",
  openai: "openai",
  claude: "claude",
  anthropic: "claude",
  qwen: "qwen",
  kibvon: "qwen",
  v0: "v0",
};

function normalizeConferenceAgentId(agent) {
  if (typeof agent !== "string") return null;
  const lower = agent.trim().toLowerCase();
  if (!lower) return null;
  return AGENT_NAME_TO_CANONICAL[lower] || lower;
}

/**
 * Default set of agents for the multi-agent conference.
 */
export const DEFAULT_MULTI_AGENT_CONFERENCE_AGENTS = ["deepseek", "gemini", "openai", "claude", "qwen", "v0"];

/**
 * Normalize an array of agent names/IDs to canonical backend IDs.
 * Removes unknown agents.
 *
 * @param {string[]} agents
 * @returns {string[]}
 */
export function normalizeConferenceAgentIds(agents) {
  if (!Array.isArray(agents) || agents.length === 0) {
    return [...DEFAULT_MULTI_AGENT_CONFERENCE_AGENTS];
  }
  const result = [...new Set(agents.map(normalizeConferenceAgentId).filter(Boolean))];
  return result.length > 0 ? result : [...DEFAULT_MULTI_AGENT_CONFERENCE_AGENTS];
}

/** Fetch configured/connected status for all frontend agents. */
export async function fetchAgentStatus() {
  return adminFetch("/api/admin/agent-status", MEDIUM_ADMIN_TIMEOUT_MS);
}

// NOTE (Backend v44 alignment): "/api/admin/agent-conference/agents" does not
// exist as a backend contract and must never be called. Conference agents are
// derived from the canonical agent status (fetchAgentStatus) together with the
// confirmed six-agent registry (DEFAULT_MULTI_AGENT_CONFERENCE_AGENTS above).

// ── Agenten-Zentrale (Operator Command Center) endpoints ───────────────
// All wrappers use adminFetch (same-origin proxy). When the backend route
// is not yet deployed, adminFetch returns meta.notFound / notImplemented so
// the UI can degrade gracefully without surfacing fake success values.

/** Live-certification summary: cert score, status, safety invariants, executor. */
export async function fetchAgentLiveCertificationSummary() {
  return adminFetch("/api/admin/agents/live-certification/summary", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** Operator overview: aggregated sessions / work-order counts + next step. */
export async function fetchAgentOperatorOverview() {
  return adminFetch("/api/admin/agents/operator/overview", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** Operator work orders: ready / waiting / blocked / completed sessions. */
export async function fetchAgentOperatorWorkOrders() {
  return adminFetch("/api/admin/agents/operator/work-orders", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** Operator approvals: pending human-in-the-loop confirmations. */
export async function fetchAgentOperatorApprovals() {
  return adminFetch("/api/admin/agents/operator/approvals", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** Operator execution readiness: whether execution is currently allowed. */
export async function fetchAgentOperatorExecutionReadiness() {
  return adminFetch("/api/admin/agents/operator/execution-readiness", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** Operator risks: blockers / risks currently flagged. */
export async function fetchAgentOperatorRisks() {
  return adminFetch("/api/admin/agents/operator/risks", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** Daily Attention summary: high-level decision for the day. */
export async function fetchAgentOperatorDailyAttentionSummary() {
  return adminFetch("/api/admin/agents/operator/daily-attention/summary", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** Daily Attention items: individual actionable items. */
export async function fetchAgentOperatorDailyAttentionItems() {
  return adminFetch("/api/admin/agents/operator/daily-attention/items", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** Daily Attention Validation: scenario validation run (read-only). */
export async function fetchAgentOperatorDailyAttentionValidation() {
  return adminFetch("/api/admin/agents/operator/daily-attention/validation/run", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** Operator Signals Validation: 7-scenario read-only validation run. */
export async function fetchAgentOperatorSignalsValidation() {
  return adminFetch("/api/admin/agents/operator/signals-validation/run", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** Repair Plans: list all repair plans (read-only). */
export async function fetchAgentOperatorRepairPlans() {
  return adminFetch("/api/admin/agents/operator/repair-plans", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** Repair Plans: get a single repair plan by id (read-only). */
export async function fetchAgentOperatorRepairPlanById(id) {
  if (!id) return { data: null, error: "Missing id", status: 400, meta: {} };
  return adminFetch(`/api/admin/agents/operator/repair-plans/${encodeURIComponent(id)}`, MEDIUM_ADMIN_TIMEOUT_MS);
}

/** Repair Plans: get repair plans for a specific work order (read-only). */
export async function fetchAgentOperatorWorkOrderRepairPlans(workOrderId) {
  if (!workOrderId) return { data: null, error: "Missing workOrderId", status: 400, meta: {} };
  return adminFetch(`/api/admin/agents/operator/work-orders/${encodeURIComponent(workOrderId)}/repair-plans`, MEDIUM_ADMIN_TIMEOUT_MS);
}

/** Review Requests: list all review requests (read-only). */
export async function fetchAgentOperatorReviewRequests() {
  return adminFetch("/api/admin/agents/operator/review-requests", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** Review Requests: get a single review request by id (read-only). */
export async function fetchAgentOperatorReviewRequestById(id) {
  if (!id) return { data: null, error: "Missing id", status: 400, meta: {} };
  return adminFetch(`/api/admin/agents/operator/review-requests/${encodeURIComponent(id)}`, MEDIUM_ADMIN_TIMEOUT_MS);
}

/** Review Requests: get review requests for a specific repair plan (read-only). */
export async function fetchAgentOperatorRepairPlanReviewRequests(repairPlanId) {
  if (!repairPlanId) return { data: null, error: "Missing repairPlanId", status: 400, meta: {} };
  return adminFetch(`/api/admin/agents/operator/repair-plans/${encodeURIComponent(repairPlanId)}/review-requests`, MEDIUM_ADMIN_TIMEOUT_MS);
}

/** Review Requests: get review requests for a specific work order (read-only). */
export async function fetchAgentOperatorWorkOrderReviewRequests(workOrderId) {
  if (!workOrderId) return { data: null, error: "Missing workOrderId", status: 400, meta: {} };
  return adminFetch(`/api/admin/agents/operator/work-orders/${encodeURIComponent(workOrderId)}/review-requests`, MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * Review Decisions – Step 4.10
 *
 * POST /api/admin/agents/operator/review-decisions/from-review-request/:reviewRequestId
 *
 * Saves a Decision Intent for a Review Request. This is a pure audit/intent
 * operation – it never executes, deploys, repairs, or merges anything.
 * executionAllowed must remain false in the response.
 *
 * @param {string} reviewRequestId
 * @param {{ decision: string, decisionReason?: string, decidedBy?: string }} payload
 */
export async function createAgentOperatorReviewDecisionIntent(reviewRequestId, payload) {
  if (!reviewRequestId) return { data: null, error: "Missing reviewRequestId", status: 400, meta: {} };
  if (!payload || typeof payload !== "object") return { data: null, error: "Missing payload", status: 400, meta: {} };
  const { decision, decisionReason, decidedBy } = payload;
  const ALLOWED_DECISIONS = ["approve_intent", "reject_intent", "needs_more_info", "defer", "manual_only"];
  if (!ALLOWED_DECISIONS.includes(decision)) {
    return { data: null, error: `Ungültige Decision: ${decision}`, status: 400, meta: {} };
  }
  return adminFetch(
    `/api/admin/agents/operator/review-decisions/from-review-request/${encodeURIComponent(reviewRequestId)}`,
    MEDIUM_ADMIN_TIMEOUT_MS,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        decision,
        decisionReason: decisionReason || "Vom Operator im Frontend protokolliert",
        decidedBy: decidedBy || null,
      }),
    },
  );
}

/** Review Decisions: list all review decisions (read-only). */
export async function fetchAgentOperatorReviewDecisions() {
  return adminFetch("/api/admin/agents/operator/review-decisions", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** Review Decisions: get a single review decision by id (read-only). */
export async function fetchAgentOperatorReviewDecisionById(id) {
  if (!id) return { data: null, error: "Missing id", status: 400, meta: {} };
  return adminFetch(`/api/admin/agents/operator/review-decisions/${encodeURIComponent(id)}`, MEDIUM_ADMIN_TIMEOUT_MS);
}

/** Review Decisions: get decisions for a specific review request (read-only). */
export async function fetchAgentOperatorReviewRequestDecisions(reviewRequestId) {
  if (!reviewRequestId) return { data: null, error: "Missing reviewRequestId", status: 400, meta: {} };
  return adminFetch(`/api/admin/agents/operator/review-requests/${encodeURIComponent(reviewRequestId)}/decisions`, MEDIUM_ADMIN_TIMEOUT_MS);
}

/** Review Decisions: get decisions for a specific work order (read-only). */
export async function fetchAgentOperatorWorkOrderReviewDecisions(workOrderId) {
  if (!workOrderId) return { data: null, error: "Missing workOrderId", status: 400, meta: {} };
  return adminFetch(`/api/admin/agents/operator/work-orders/${encodeURIComponent(workOrderId)}/review-decisions`, MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * Execution Gates – Step 4.12 (read-only, no write actions)
 *
 * GET /api/admin/agents/operator/execution-gates/for-decision/:decisionId
 * Returns the gate evaluation for a specific Decision Intent.
 * executionAllowed is always false in the response.
 *
 * @param {string} decisionId
 */
export async function fetchAgentOperatorExecutionGateForDecision(decisionId) {
  if (!decisionId) return { data: null, error: "Missing decisionId", status: 400, meta: {} };
  return adminFetch(`/api/admin/agents/operator/execution-gates/for-decision/${encodeURIComponent(decisionId)}`, MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * GET /api/admin/agents/operator/execution-gates/for-review-request/:reviewRequestId
 * Returns the gate evaluation for a specific Review Request.
 * executionAllowed is always false in the response.
 *
 * @param {string} reviewRequestId
 */
export async function fetchAgentOperatorExecutionGateForReviewRequest(reviewRequestId) {
  if (!reviewRequestId) return { data: null, error: "Missing reviewRequestId", status: 400, meta: {} };
  return adminFetch(`/api/admin/agents/operator/execution-gates/for-review-request/${encodeURIComponent(reviewRequestId)}`, MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * GET /api/admin/agents/operator/execution-gates/health
 * Returns the health status of the Execution Gate system (read-only).
 */
export async function fetchAgentOperatorExecutionGateHealth() {
  return adminFetch("/api/admin/agents/operator/execution-gates/health", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * GET /api/admin/agents/operator/execution-gates/config
 * Returns the current Execution Gate configuration (read-only).
 */
export async function fetchAgentOperatorExecutionGateConfig() {
  return adminFetch("/api/admin/agents/operator/execution-gates/config", MEDIUM_ADMIN_TIMEOUT_MS);
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 4.14 – Execution Plan Drafts (read-only display, no execution)
//
// POST creates only a draft – nothing is executed, deployed, or merged.
// GET endpoints fetch existing drafts for read-only display.
// Safety: executionAllowed is always false; canExecute is always false.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * POST /api/admin/agents/operator/execution-plan-drafts/from-gate/:gateId
 *
 * Creates an Execution Plan Draft from an Execution Gate that is
 * eligible_for_execution_plan. The draft describes what would theoretically
 * need to happen – nothing is executed.
 *
 * @param {string} gateId
 */
export async function createAgentOperatorExecutionPlanDraftFromGate(gateId) {
  if (!gateId || typeof gateId !== "string") {
    return { data: null, error: "Missing gateId", status: 400, meta: {} };
  }
  return adminFetch(
    `/api/admin/agents/operator/execution-plan-drafts/from-gate/${encodeURIComponent(gateId)}`,
    MEDIUM_ADMIN_TIMEOUT_MS,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) },
  );
}

/**
 * POST /api/admin/agents/operator/execution-plan-drafts/from-decision/:decisionId
 *
 * Creates an Execution Plan Draft from a Decision Intent.
 * Nothing is executed; draft is read-only.
 *
 * @param {string} decisionId
 */
export async function createAgentOperatorExecutionPlanDraftFromDecision(decisionId) {
  if (!decisionId || typeof decisionId !== "string") {
    return { data: null, error: "Missing decisionId", status: 400, meta: {} };
  }
  return adminFetch(
    `/api/admin/agents/operator/execution-plan-drafts/from-decision/${encodeURIComponent(decisionId)}`,
    MEDIUM_ADMIN_TIMEOUT_MS,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) },
  );
}

/**
 * GET /api/admin/agents/operator/execution-plan-drafts
 *
 * Returns a list of all Execution Plan Drafts (read-only).
 */
export async function fetchAgentOperatorExecutionPlanDrafts() {
  return adminFetch("/api/admin/agents/operator/execution-plan-drafts", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * GET /api/admin/agents/operator/execution-plan-drafts/:id
 *
 * Returns a single Execution Plan Draft by ID (read-only).
 *
 * @param {string} id
 */
export async function fetchAgentOperatorExecutionPlanDraftById(id) {
  if (!id || typeof id !== "string") {
    return { data: null, error: "Missing id", status: 400, meta: {} };
  }
  return adminFetch(
    `/api/admin/agents/operator/execution-plan-drafts/${encodeURIComponent(id)}`,
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
}

/**
 * GET /api/admin/agents/operator/execution-plan-drafts/health
 *
 * Returns the health status of the Execution Plan Draft system (read-only).
 */
export async function fetchAgentOperatorExecutionPlanDraftHealth() {
  return adminFetch("/api/admin/agents/operator/execution-plan-drafts/health", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * GET /api/admin/agents/operator/execution-plan-drafts/config
 *
 * Returns the current Execution Plan Draft configuration (read-only).
 */
export async function fetchAgentOperatorExecutionPlanDraftConfig() {
  return adminFetch("/api/admin/agents/operator/execution-plan-drafts/config", MEDIUM_ADMIN_TIMEOUT_MS);
}

// ── Step 4.13.8: Agent Pending Tasks (Silent Data Loss signals) ───────────────
// All helpers are strictly read-only (GET only).
// POST /record must NEVER be called from the frontend.

/**
 * GET /api/admin/agents/pending-tasks
 *
 * Returns the list of agent pending tasks (read-only).
 */
export async function fetchAgentPendingTasks() {
  return adminFetch("/api/admin/agents/pending-tasks", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * GET /api/admin/agents/pending-tasks/overview
 *
 * Returns a summary overview of pending task counts by severity (read-only).
 */
export async function fetchAgentPendingTasksOverview() {
  return adminFetch("/api/admin/agents/pending-tasks/overview", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * GET /api/admin/agents/pending-tasks/health
 *
 * Returns health status of the pending task signal system (read-only).
 */
export async function fetchAgentPendingTasksHealth() {
  return adminFetch("/api/admin/agents/pending-tasks/health", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * GET /api/admin/agents/pending-tasks/config
 *
 * Returns the current pending task signal configuration (read-only).
 */
export async function fetchAgentPendingTasksConfig() {
  return adminFetch("/api/admin/agents/pending-tasks/config", MEDIUM_ADMIN_TIMEOUT_MS);
}

// ── Step 4.13.17 – Agent Provider Registry (read-only GET helpers) ─────────��─

/**
 * GET /api/admin/agents/provider-registry
 *
 * Returns the full agent provider registry list (read-only).
 * Unknown or broken providers are surfaced honestly – no silent fallbacks.
 */
export async function fetchAgentProviderRegistry() {
  return adminFetch("/api/admin/agents/provider-registry", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * GET /api/admin/agents/provider-registry/health
 *
 * Returns the health status of the agent provider registry (read-only).
 */
export async function fetchAgentProviderRegistryHealth() {
  return adminFetch("/api/admin/agents/provider-registry/health", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * GET /api/admin/agents/provider-registry/config
 *
 * Returns the current configuration of the agent provider registry (read-only).
 */
export async function fetchAgentProviderRegistryConfig() {
  return adminFetch("/api/admin/agents/provider-registry/config", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * GET /api/admin/agents/provider-registry/:providerKey
 *
 * Direct lookup of a single provider entry by key.
 * Returns a 404 / unknown_provider response for unknown keys – never a silent 200 fallback.
 * Failed lookups must be treated as unavailable/unknown_provider by the caller, not as a crash.
 *
 * @param {string} providerKey – canonical provider key (e.g. "v0", "openai")
 */
export async function fetchAgentProviderRegistryByKey(providerKey) {
  if (!providerKey || typeof providerKey !== "string") {
    return { data: null, error: "Kein Provider-Key angegeben.", status: 0, meta: {} };
  }
  return adminFetch(
    `/api/admin/agents/provider-registry/${encodeURIComponent(providerKey)}`,
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
}

/**
 * GET /api/admin/agents/provider-registry/graph
 *
 * Step 4.13.21 – Graph Layout Backend Sync (read-only).
 *
 * Returns the graph metadata bundle from the backend registry:
 *   { providers, layouts, relationships, config }
 *
 * Rules:
 *   - GET only – no POST / PUT / PATCH / DELETE / execute / deploy / merge.
 *   - No fake data is generated when the endpoint is unavailable.
 *   - Errors are returned as a structured envelope; callers must surface them
 *     as "Registry-Graph nicht verfügbar" – never as a fake-healthy state.
 *   - The caller must normalize the response with normalizeAgentProviderGraphMetadata().
 */
export async function fetchAgentProviderRegistryGraph() {
  return adminFetch("/api/admin/agents/provider-registry/graph", MEDIUM_ADMIN_TIMEOUT_MS);
}

// ── Step 4.13.23 – Domain Pack Sync (read-only) ──────────────────────────────

/**
 * GET /api/admin/agents/domain-packs
 *
 * Returns the list of available agent domain packs (read-only).
 * No POST / PUT / PATCH / DELETE variants exist.
 */
export async function fetchAgentDomainPacks() {
  return adminFetch("/api/admin/agents/domain-packs", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * GET /api/admin/agents/domain-packs/config
 *
 * Returns the domain-packs configuration bundle, including the default active pack key.
 */
export async function fetchAgentDomainPacksConfig() {
  return adminFetch("/api/admin/agents/domain-packs/config", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * GET /api/admin/agents/domain-packs/default
 *
 * Returns the default/active domain pack descriptor (read-only).
 */
export async function fetchDefaultAgentDomainPack() {
  return adminFetch("/api/admin/agents/domain-packs/default", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * GET /api/admin/agents/domain-packs/:packKey
 *
 * Direct lookup of a single domain pack by key.
 * Returns a structured error shape for unknown keys – never a silent 200 fallback.
 *
 * @param {string} packKey – canonical pack key (e.g. "core_hqs", "commerce_hybrid")
 */
export async function fetchAgentDomainPackByKey(packKey) {
  if (!packKey || typeof packKey !== "string") {
    return { data: null, error: "Kein Pack-Key angegeben.", status: 0, meta: {} };
  }
  return adminFetch(
    `/api/admin/agents/domain-packs/${encodeURIComponent(packKey)}`,
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
}

/**
 * GET /api/admin/agents/domain-packs/:packKey/graph
 *
 * Returns the agent-relationship graph for a specific domain pack (read-only).
 * Returns a structured error shape when the pack or its graph is unavailable.
 *
 * @param {string} packKey – canonical pack key (e.g. "core_hqs", "commerce_hybrid")
 */
export async function fetchAgentDomainPackGraph(packKey) {
  if (!packKey || typeof packKey !== "string") {
    return { data: null, error: "Kein Pack-Key für Graph angegeben.", status: 0, meta: {} };
  }
  return adminFetch(
    `/api/admin/agents/domain-packs/${encodeURIComponent(packKey)}/graph`,
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
}


function resolveConferenceMessageInput(input) {
  if (input && typeof input === "object" && !Array.isArray(input)) {
    return (
      (typeof input.message === "string" && input.message) ||
      (typeof input.userMessage === "string" && input.userMessage) ||
      (typeof input.text === "string" && input.text) ||
      (typeof input.prompt === "string" && input.prompt) ||
      ""
    );
  }
  return typeof input === "string" ? input : "";
}

/**
 * Send a message to the multi-agent conference via the new backend routing endpoint.
 *
 * POST /api/admin/deepseek/agent-bridge/conference-multi-agent-message
 *
 * @param {string|object} message - The user message; objects may use message/userMessage/text/prompt
 * @param {string[]} [agents] - Canonical agent IDs (defaults to all conference agents)
 * @param {string} [conferenceId] - Stable session ID; if omitted a new one is generated
 * @param {object} [options] - Optional leadAgent/replyToMessageId/title fields
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function postAdminConferenceMultiAgentMessage(
  message,
  agents = DEFAULT_MULTI_AGENT_CONFERENCE_AGENTS,
  conferenceId,
  options = {},
) {
  const token = await getAccessToken();
  const canonicalAgents = normalizeConferenceAgentIds(agents);
  const resolvedMessage = resolveConferenceMessageInput(message);
  const sourceOptions = message && typeof message === "object" && !Array.isArray(message) ? message : options;
  const payload = {
    conferenceId:
      conferenceId ||
      (typeof sourceOptions?.conferenceId === "string" && sourceOptions.conferenceId) ||
      (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? `admin-conference-${crypto.randomUUID()}`
        : `admin-conference-${Date.now()}-${Math.random().toString(36).slice(2)}`),
    message: resolvedMessage,
    selectedAgents: canonicalAgents,
  };
  for (const key of ["leadAgent", "replyToMessageId", "title"]) {
    if (typeof sourceOptions?.[key] === "string" && sourceOptions[key]) {
      payload[key] = sourceOptions[key];
    }
  }
  if (import.meta.env.DEV) {
    console.log(
      "REQUEST →",
      "/api/admin/deepseek/agent-bridge/conference-multi-agent-message",
      payload,
    );
  }
  return apiFetch(
    "/api/admin/deepseek/agent-bridge/conference-multi-agent-message",
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    AGENT_TIMEOUT_MS,
  );
}

/**
 * Fetch routing diagnostics for the agent conference.
 * Shows which agents are available (API key present) and which are missing.
 *
 * GET /api/admin/agent-conference/routing-diagnostics
 *
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function getAdminConferenceRoutingDiagnostics() {
  return adminFetch("/api/admin/agent-conference/routing-diagnostics");
}

// ── Agent Conference Session Management ───────────────────────────────────

/**
 * Fetch all stored conference sessions.
 * GET /api/admin/agent-conference/sessions
 *
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function getAgentConferenceSessions() {
  return adminFetch("/api/admin/agent-conference/sessions");
}

/**
 * Fetch a single conference session by ID.
 * GET /api/admin/agent-conference/sessions/:conferenceId
 *
 * @param {string} conferenceId
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function getAgentConferenceSession(conferenceId) {
  return adminFetch(
    `/api/admin/agent-conference/sessions/${encodeURIComponent(conferenceId)}`,
  );
}

/**
 * Create a new conference session on the backend.
 * POST /api/admin/agent-conference/sessions
 *
 * @param {string[]} [selectedAgents] - Canonical agent IDs for this session
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function createAgentConferenceSession(
  selectedAgents = DEFAULT_MULTI_AGENT_CONFERENCE_AGENTS,
) {
  const token = await getAccessToken();
  return apiFetch(
    "/api/admin/agent-conference/sessions",
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ participants: normalizeConferenceAgentIds(selectedAgents) }),
    },
    AGENT_TIMEOUT_MS,
  );
}

/**
 * Update an existing conference session (e.g. persist selected agents).
 * PATCH /api/admin/agent-conference/sessions/:conferenceId
 *
 * @param {string} conferenceId
 * @param {object} patch - Partial update (e.g. { selectedAgents })
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function updateAgentConferenceSession(conferenceId, patch) {
  const token = await getAccessToken();
  return apiFetch(
    `/api/admin/agent-conference/sessions/${encodeURIComponent(conferenceId)}`,
    {
      method: "PATCH",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    },
    AGENT_TIMEOUT_MS,
  );
}

/**
 * Fetch the agent-conference health/diagnostics summary.
 * GET /api/admin/agent-conference/health
 *
 * Returns a structured health snapshot including overall status, routing
 * details, risks/warnings, and the suggested next action.
 *
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function getAgentConferenceHealth() {
  const token = await getAccessToken();
  return apiFetch(
    "/api/admin/agent-conference/health",
    {
      method: "GET",
      _accessToken: token,
      _sameOrigin: true,
    },
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
}

/**
 * Fetch proposal approval/readiness state for the existing conference chat.
 * GET /api/admin/conference/proposal-readiness
 */
export async function fetchConferenceProposalReadiness() {
  const result = await adminFetch(
    "/api/admin/conference/proposal-readiness",
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
  return mapConferenceProposalError(result);
}

/**
 * Approve a conference proposal decision.
 * POST /api/admin/conference/proposals/:decisionId/approve
 */
export async function approveConferenceProposal(decisionId, approvalText = "") {
  const token = await getAccessToken();
  const result = await apiFetch(
    `/api/admin/conference/proposals/${encodeURIComponent(decisionId)}/approve`,
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ approvalText }),
    },
    DEFAULT_TIMEOUT_MS,
  );
  return mapConferenceProposalError(result);
}

/**
 * Reject a conference proposal decision.
 * POST /api/admin/conference/proposals/:decisionId/reject
 */
export async function rejectConferenceProposal(decisionId, reason = "") {
  if (!decisionId || typeof decisionId !== "string") {
    return {
      data: null,
      error: "decisionId fehlt – Ablehnung kann nicht gespeichert werden.",
      status: 0,
      meta: { missingDecisionId: true },
    };
  }
  const token = await getAccessToken();
  const result = await apiFetch(
    `/api/admin/conference/proposals/${encodeURIComponent(decisionId)}/reject`,
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason }),
    },
    DEFAULT_TIMEOUT_MS,
  );
  return mapConferenceProposalError(result);
}

/**
 * Approve a conference proposal as the current user (Step 29).
 * POST /api/admin/conference/proposals/:decisionId/user-approve
 *
 * @param {string} decisionId - The proposal decision ID (required)
 * @param {object} [payload]  - Optional payload; no secrets are logged
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function approveConferenceProposalAsUser(decisionId, payload = {}) {
  if (!decisionId || typeof decisionId !== "string") {
    return {
      data: null,
      error: "decisionId fehlt – Freigabe kann nicht gespeichert werden.",
      status: 0,
      meta: { missingDecisionId: true },
    };
  }
  const token = await getAccessToken();
  const safePayload = payload && typeof payload === "object" && !Array.isArray(payload) ? payload : {};
  const result = await apiFetch(
    `/api/admin/conference/proposals/${encodeURIComponent(decisionId)}/user-approve`,
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(safePayload),
    },
    DEFAULT_TIMEOUT_MS,
  );
  return mapApprovalGateError(result);
}

// ── Conference System Issues (Step 14D/14E) ────────────────────────────────

/**
 * Check whether the system-issue detection backend endpoints are available
 * and configured.
 * GET /api/admin/conference/system-issues/readiness
 *
 * Returns 404 silently when the endpoint is not yet deployed; callers check
 * meta.notFound to distinguish unavailability from errors.
 *
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function fetchConferenceSystemIssueReadiness() {
  return adminFetch(
    "/api/admin/conference/system-issues/readiness",
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
}

/**
 * Fetch the current list of detected system issues as conference-chat messages.
 * GET /api/admin/conference/system-issues
 *
 * Returns 404 silently when the endpoint is not yet deployed.
 *
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function fetchConferenceSystemIssues() {
  return adminFetch(
    "/api/admin/conference/system-issues",
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
}

/**
 * Ingest detected system issues into the conference chat as agent messages.
 * POST /api/admin/conference/system-issues/ingest
 *
 * After a successful call the caller should reload the conference session so
 * newly added system-issue messages become visible in the chat thread.
 *
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function ingestConferenceSystemIssues() {
  const token = await getAccessToken();
  return apiFetch(
    "/api/admin/conference/system-issues/ingest",
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
}

// ── Conference System-Issue Proposal (Step 14F/14G) ──���────────────────────────

/**
 * Create an agent proposal from a single system issue.
 * POST /api/admin/conference/system-issues/:issueKey/propose
 *
 * Returns 404 silently when the endpoint is not yet deployed.
 * 401/403 → German error "Admin-Berechtigung fehlt".
 *
 * @param {string} issueKey - The unique key of the system issue
 * @param {object} [payload] - Optional body, e.g. { source: "conference_chat" }
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function createProposalFromSystemIssue(issueKey, payload = {}) {
  if (!issueKey) {
    return { data: null, error: "issueKey fehlt – Vorschlag kann nicht erstellt werden.", status: 0, meta: {} };
  }
  const token = await getAccessToken();
  const result = await apiFetch(
    `/api/admin/conference/system-issues/${encodeURIComponent(issueKey)}/propose`,
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload || {}),
    },
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
  return mapConferenceProposalError(result);
}

/**
 * Create agent proposals from all currently detected system issues.
 * POST /api/admin/conference/system-issues/propose-all
 *
 * Returns 404 silently when the endpoint is not yet deployed.
 *
 * @param {object} [payload] - Optional body
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function createProposalsFromAllSystemIssues(payload = {}) {
  const token = await getAccessToken();
  const result = await apiFetch(
    "/api/admin/conference/system-issues/propose-all",
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload || {}),
    },
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
  return mapConferenceProposalError(result);
}

/**
 * Fetch readiness / configuration state for issue-based proposals.
 * GET /api/admin/conference/issue-proposal-readiness
 *
 * Returns 404 silently when the endpoint is not yet deployed; callers check
 * meta.notFound to distinguish unavailability from errors.
 *
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function fetchIssueProposalReadiness() {
  return adminFetch(
    "/api/admin/conference/issue-proposal-readiness",
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
}

// ── Conference Control-Center Issues (new Step) ──────────────────────────────

/**
 * Fetch current Control-Center / Data-Quality issues.
 * GET /api/admin/conference/control-center-issues
 *
 * Returns 404 silently when not yet deployed.
 */
export async function fetchConferenceControlCenterIssues() {
  return adminFetch("/api/admin/conference/control-center-issues", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * Ingest Control-Center issues into the conference as cases + messages.
 * POST /api/admin/conference/control-center-issues/ingest
 *
 * Response typically includes: issuesIngested, casesCreated, criticalCases.
 * Returns 404 when the endpoint is not yet deployed.
 *
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function ingestConferenceControlCenterIssues() {
  const token = await getAccessToken();
  return apiFetch(
    "/api/admin/conference/control-center-issues/ingest",
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
}

/**
 * List active Control-Center conference cases.
 * GET /api/admin/conference/control-center-cases
 *
 * Returns 404 silently when not yet deployed.
 */
export async function fetchConferenceControlCenterCases() {
  return adminFetch("/api/admin/conference/control-center-cases", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * Fetch a single Conference Control-Center case lifecycle.
 * GET /api/admin/conference/cases/:caseId
 */
export async function fetchConferenceCase(caseId) {
  if (!caseId) {
    return { data: null, error: "caseId fehlt – Fall kann nicht geladen werden.", status: 0, meta: {} };
  }
  const result = await adminFetch(
    `/api/admin/conference/cases/${encodeURIComponent(caseId)}`,
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
  return mapConferenceCaseLifecycleError(result);
}

/**
 * Fetch chronological lifecycle timeline for a case.
 * GET /api/admin/conference/cases/:caseId/timeline
 */
export async function fetchConferenceCaseTimeline(caseId) {
  if (!caseId) {
    return { data: { events: [] }, error: "caseId fehlt – Verlauf kann nicht geladen werden.", status: 0, meta: {} };
  }
  const result = await adminFetch(
    `/api/admin/conference/cases/${encodeURIComponent(caseId)}/timeline`,
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
  return mapConferenceCaseLifecycleError(result);
}

/**
 * Triage a detected Conference Control-Center case.
 * POST /api/admin/conference/cases/:caseId/triage
 */
export async function triageConferenceCase(caseId, payload = {}) {
  if (!caseId) {
    return { data: null, error: "caseId fehlt – Fall kann nicht eingeordnet werden.", status: 0, meta: {} };
  }
  const token = await getAccessToken();
  const result = await apiFetch(
    `/api/admin/conference/cases/${encodeURIComponent(caseId)}/triage`,
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload || {}),
    },
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
  return mapConferenceCaseLifecycleError(result);
}

/**
 * Ignore a Conference Control-Center case.
 * POST /api/admin/conference/cases/:caseId/ignore
 */
export async function ignoreConferenceCase(caseId, reason = "Vom Operator ignoriert") {
  if (!caseId) {
    return { data: null, error: "caseId fehlt – Fall kann nicht ignoriert werden.", status: 0, meta: {} };
  }
  const token = await getAccessToken();
  const result = await apiFetch(
    `/api/admin/conference/cases/${encodeURIComponent(caseId)}/ignore`,
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason: safeApiText(reason, "Vom Operator ignoriert") }),
    },
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
  return mapConferenceCaseLifecycleError(result);
}

/**
 * Check whether a prepared/executed case is resolved.
 * POST /api/admin/conference/cases/:caseId/check-resolution
 */
export async function checkConferenceCaseResolution(caseId) {
  if (!caseId) {
    return { data: null, error: "caseId fehlt – Lösung kann nicht geprüft werden.", status: 0, meta: {} };
  }
  const token = await getAccessToken();
  const result = await apiFetch(
    `/api/admin/conference/cases/${encodeURIComponent(caseId)}/check-resolution`,
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
  return mapConferenceCaseLifecycleError(result);
}

/**
 * Fetch aggregate lifecycle/readiness counts for conference cases.
 * GET /api/admin/conference/cases/readiness
 */
export async function fetchConferenceCasesReadiness() {
  const result = await adminFetch("/api/admin/conference/cases/readiness", MEDIUM_ADMIN_TIMEOUT_MS);
  return mapConferenceCaseLifecycleError(result);
}

/**
 * Create an agent proposal from a Control-Center case.
 * POST /api/admin/conference/cases/:caseId/propose
 *
 * 404 → "Backend-Endpunkt noch nicht verfügbar"
 * 401/403 → "Admin-Berechtigung fehlt"
 * Missing caseId → immediate German error without network call.
 *
 * @param {string} caseId - The unique ID of the control-center case
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function createConferenceProposalFromCase(caseId) {
  if (!caseId) {
    return { data: null, error: "caseId fehlt – Vorschlag kann nicht erstellt werden.", status: 0, meta: {} };
  }
  const token = await getAccessToken();
  const result = await apiFetch(
    `/api/admin/conference/cases/${encodeURIComponent(caseId)}/propose`,
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
  return mapConferenceProposalError(result);
}

/**
 * Fetch Control-Center readiness including activeCases[], counts and routing summary.
 * GET /api/admin/conference/control-center-readiness
 *
 * Returns 404 silently when not yet deployed.
 */
export async function fetchConferenceControlCenterReadiness() {
  return adminFetch("/api/admin/conference/control-center-readiness", MEDIUM_ADMIN_TIMEOUT_MS);
}

// ── Qwen/Kibvon Coder-Agent (Step 26 / Step 32) ────────────────────────────────

/**
 * Send a direct chat message to the Qwen coder-agent (Step 32).
 * POST /api/admin/qwen/agent-chat
 *
 * Used by AdminQwenAgentView for standalone Qwen conversations.
 * Does not require a decisionId — messages are sent as plain chat.
 *
 * Guards:
 *   - 401/403 → auth error surfaced as German message.
 *   - 404 → endpoint not yet deployed (meta.notFound).
 *   - Network/timeout errors → structured error without crashing the app.
 *
 * @param {object} payload  e.g. { conversationId, message, mode }
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function postAdminQwenAgentChat(payload) {
  const token = await getAccessToken();
  return apiFetch(
    "/api/admin/qwen/agent-chat",
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    AGENT_TIMEOUT_MS,
  );
}

/**
 * Fetch Qwen/Kibvon coder-agent status.
 * GET /api/admin/qwen/status
 *
 * Returns a structured status object including:
 *   available, configured, missing, role, defaultEnabled,
 *   conferenceEligible, canRunCoderTask, requiresOpenAiLeadApproval,
 *   requiresUserApproval, canAutoExecute
 *
 * Returns 404 silently when the endpoint is not yet deployed.
 *
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function fetchQwenStatus() {
  return adminFetch("/api/admin/qwen/status", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * Start a Qwen coder-task (report-only, no automatic code execution).
 * POST /api/admin/qwen/coder-task
 *
 * Guards:
 *   - Missing decisionId → immediate German error without network call.
 *   - 401/403 → auth error surfaced as German message.
 *   - 404 → endpoint not yet deployed (meta.notFound).
 *   - Blocked by backend gate → data.blocked = true with reason and requiredGates[].
 *   - Network/timeout errors → structured error without crashing the app.
 *
 * @param {string} decisionId - Required. Conference decision identifier.
 * @param {object} [payload]  - Optional additional fields, e.g. { conferenceId, source }.
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function startQwenCoderTask(decisionId, payload = {}) {
  if (!decisionId) {
    return {
      data: null,
      error: "decisionId fehlt – Qwen-Coder-Auftrag kann nicht gestartet werden.",
      status: 0,
      meta: {},
    };
  }
  const token = await getAccessToken();
  return apiFetch(
    "/api/admin/qwen/coder-task",
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      // Spread caller payload first; decisionId last so it always wins.
      body: JSON.stringify({ ...payload, decisionId }),
    },
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
}

/**
 * Request an OpenAI final summary for a completed Qwen coder-task.
 * POST /api/admin/qwen/coder-task/:decisionId/final-summary
 *
 * Guards:
 *   - Missing decisionId → immediate German error without network call.
 *   - 400/401/403/404/422/500/503 → structured German error surfaced without crashing.
 *   - Network/timeout errors → structured error without crashing the app.
 *   - Never logs secrets.
 *
 * @param {string} decisionId - Required. The decisionId returned by the Qwen coder task.
 * @param {object} [payload]  - Optional additional fields, e.g. { source }.
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function requestQwenOpenAiFinalSummary(decisionId, payload = {}) {
  if (!decisionId) {
    return {
      data: null,
      error: "decisionId fehlt – OpenAI-Endzusammenfassung kann nicht erstellt werden.",
      status: 0,
      meta: {},
    };
  }
  const token = await getAccessToken();
  return apiFetch(
    `/api/admin/qwen/coder-task/${encodeURIComponent(decisionId)}/final-summary`,
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...payload }),
    },
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
}

/**
 * Request an OpenAI lead evaluation for a conference proposal.
 * POST /api/admin/conference/proposals/:decisionId/lead-evaluate
 *
 * Step 30: Allows the user to explicitly request an OpenAI lead evaluation.
 * The user may only trigger the evaluation; they cannot fake or force-approve it.
 * The backend is the single source of truth for leadApprovalStatus.
 *
 * Guards:
 *   - Missing decisionId → immediate German error without network call.
 *   - 401/403/404/409/422/500 → structured German error without crashing.
 *   - Network/timeout errors → structured error without crashing the app.
 *   - Never logs secrets.
 *   - Never sends leadApprovalStatus as "lead_approved" in the payload.
 *   - No automatic follow-up call after success.
 *
 * @param {string} decisionId - Required. The decisionId of the proposal to evaluate.
 * @param {object} [payload]  - Optional. Defaults to { source: "conference_ui", requestedBy: "user" }.
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function requestOpenAiLeadEvaluation(decisionId, payload = {}) {
  if (!decisionId) {
    return {
      data: null,
      error: "decisionId fehlt – OpenAI-Bewertung kann nicht angefordert werden.",
      status: 0,
      meta: {},
    };
  }
  const token = await getAccessToken();
  const safePayload = {
    source: "conference_ui",
    requestedBy: "user",
    ...payload,
  };
  // Security: never allow the frontend to fake leadApprovalStatus
  delete safePayload.leadApprovalStatus;
  delete safePayload.lead_approval_status;

  return apiFetch(
    `/api/admin/conference/proposals/${encodeURIComponent(decisionId)}/lead-evaluate`,
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(safePayload),
    },
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
}

/**
 * Archive a conference session.
 * POST /api/admin/agent-conference/sessions/:conferenceId/archive
 *
 * @param {string} conferenceId
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function archiveAgentConferenceSession(conferenceId) {
  const token = await getAccessToken();
  return apiFetch(
    `/api/admin/agent-conference/sessions/${encodeURIComponent(conferenceId)}/archive`,
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
    DEFAULT_TIMEOUT_MS,
  );
}

// ── Claude Runtime Architect ──────────────────────────────────────────────

/**
 * GET /api/admin/runtime-architect/status
 * Returns the current status of the Claude Runtime Architect.
 */
export async function fetchRuntimeArchitectStatus() {
  return adminFetch("/api/admin/runtime-architect/status", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * GET /api/admin/runtime-architect/audit
 * Returns the audit trail of all runtime changes made by Claude.
 */
export async function fetchRuntimeArchitectAudit() {
  return adminFetch("/api/admin/runtime-architect/audit", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * GET /api/admin/job-config
 * Returns the current job configuration (parameters, sources, limits).
 */
export async function fetchJobConfig() {
  return adminFetch("/api/admin/job-config", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * GET /api/admin/job-config/recommendations
 * Returns Claude's open recommendations for job parameter adjustments.
 */
export async function fetchJobConfigRecommendations() {
  return adminFetch("/api/admin/job-config/recommendations", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * GET /api/admin/system-truth
 * Returns the canonical system truth including runtimeArchitect block.
 */
export async function fetchSystemTruth() {
  return adminFetch("/api/admin/system-truth", MEDIUM_ADMIN_TIMEOUT_MS);
}

// ── DeepSeek Data Repair Architect endpoints ───────────────────────────────

/**
 * GET /api/admin/deepseek/data-repair/status
 * Returns the current status of the DeepSeek Data Repair Architect.
 */
export async function fetchDeepSeekDataRepairStatus() {
  return adminFetch("/api/admin/deepseek/data-repair/status", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * GET /api/admin/deepseek/data-repair/recommendations
 * Returns DeepSeek data-repair recommendations with Claude review decisions.
 */
export async function fetchDeepSeekDataRepairRecommendations() {
  return adminFetch("/api/admin/deepseek/data-repair/recommendations", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * GET /api/admin/deepseek/data-repair/queue
 * Returns the current repair queue (planned / prepared repair items).
 */
export async function fetchDeepSeekDataRepairQueue() {
  return adminFetch("/api/admin/deepseek/data-repair/queue", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * GET /api/admin/deepseek/data-repair/audit
 * Returns the audit trail for data repair actions.
 */
export async function fetchDeepSeekDataRepairAudit() {
  return adminFetch("/api/admin/deepseek/data-repair/audit", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * POST /api/admin/deepseek/data-repair/analyze
 * Triggers a new DeepSeek data-flow analysis.
 * Returns { ok: true } or an error when the backend responds with an error.
 * A 501 response means auto-repair is disabled in Step 1 and must not be
 * treated as an error in the UI.
 */
export async function runDeepSeekDataRepairAnalyze() {
  const token = await getAccessToken();
  return apiFetch("/api/admin/deepseek/data-repair/analyze", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  }, MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * POST /api/admin/deepseek/data-repair/run-autonomous-scan
 * Löst denselben Lauf aus wie der geplante Cron-Job (06:30 UTC): DeepSeek findet
 * Datenlücken, Claude prüft unabhängig, Qwen erstellt für bestätigte
 * RECOMMEND_CODE_PATCH-Funde einen Draft-PR. Läuft FIRE-AND-FORGET im Hintergrund –
 * die Antwort kommt sofort (202 Accepted), lange bevor der Job fertig ist.
 * Gibt { ok, success, accepted, message } zurück oder einen Fehler. Es gibt KEINEN
 * eigenen Status-Endpunkt für diesen Job – das Ergebnis erscheint später in der
 * regulären Repair Queue (fetchDeepSeekDataRepairQueue).
 */
export async function runAutonomousDataRepairScan() {
  const token = await getAccessToken();
  return apiFetch("/api/admin/deepseek/data-repair/run-autonomous-scan", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  }, AUTONOMOUS_SCAN_TIMEOUT_MS);
}

// ── Agent Work Sessions endpoints ──────────────────────────────────────────

/**
 * GET /api/admin/agent-work/sessions
 * Returns all agent work sessions (all statuses).
 */
export async function fetchAgentWorkSessions() {
  return adminFetch("/api/admin/agent-work/sessions", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * GET /api/admin/agent-work/sessions?status=active
 * Returns only active agent work sessions.
 */
export async function fetchActiveAgentWorkSessions() {
  return adminFetch("/api/admin/agent-work/sessions?status=active", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * GET /api/admin/agent-work/sessions?recent=true
 * Returns recently completed or modified agent work sessions.
 */
export async function fetchRecentAgentWorkSessions() {
  return adminFetch("/api/admin/agent-work/sessions?recent=true", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * GET /api/admin/agent-work/sessions/:id
 * Returns full detail of a single agent work session.
 */
export async function fetchAgentWorkSessionDetail(id) {
  if (!id) return { data: null, error: "Keine Session-ID angegeben.", status: 0, meta: {} };
  return adminFetch(`/api/admin/agent-work/sessions/${encodeURIComponent(id)}`, MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * GET /api/admin/agent-work/sessions/:id/events
 * Returns the event timeline for a single agent work session.
 */
export async function fetchAgentWorkSessionEvents(id) {
  if (!id) return { data: null, error: "Keine Session-ID angegeben.", status: 0, meta: {} };
  return adminFetch(`/api/admin/agent-work/sessions/${encodeURIComponent(id)}/events`, MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * POST /api/admin/agent-work/sessions/:id/cancel
 * Cancels a running agent work session.
 */
export async function cancelAgentWorkSession(id) {
  if (!id) return { data: null, error: "Keine Session-ID angegeben.", status: 0, meta: {} };
  const token = await getAccessToken();
  return apiFetch(`/api/admin/agent-work/sessions/${encodeURIComponent(id)}/cancel`, {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  }, MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * POST /api/admin/agent-work/sessions/:id/retry
 * Retries a failed or blocked agent work session.
 */
export async function retryAgentWorkSession(id) {
  if (!id) return { data: null, error: "Keine Session-ID angegeben.", status: 0, meta: {} };
  const token = await getAccessToken();
  return apiFetch(`/api/admin/agent-work/sessions/${encodeURIComponent(id)}/retry`, {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  }, MEDIUM_ADMIN_TIMEOUT_MS);
}

// ── Chat-first Agent Work endpoints (slash style) ──────────────────────────
//
// New unified surface used by the reusable AgentWorkChat workspace and by
// all future agent views (DeepSeek, Claude, Gemini, OpenAI, Qwen, v0).
// All routes are admin-only and go through the same adminFetch proxy –
// no secret is exposed to the browser.

/** GET /api/admin/agent-work/sessions */
export async function fetchAgentWorkSessionsV2() {
  return adminFetch("/api/admin/agent-work/sessions", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** GET /api/admin/agent-work/active */
export async function fetchAgentWorkActiveV2() {
  return adminFetch("/api/admin/agent-work/active", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** GET /api/admin/agent-work/recent */
export async function fetchAgentWorkRecentV2() {
  return adminFetch("/api/admin/agent-work/recent", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** GET /api/admin/agent-work/sessions/:id */
export async function fetchAgentWorkSessionV2(id) {
  if (!id) return { data: null, error: "Keine Session-ID angegeben.", status: 0, meta: {} };
  return adminFetch(
    `/api/admin/agent-work/sessions/${encodeURIComponent(id)}`,
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
}

/** GET /api/admin/agent-work/sessions/:id/events */
export async function fetchAgentWorkSessionEventsV2(id) {
  if (!id) return { data: null, error: "Keine Session-ID angegeben.", status: 0, meta: {} };
  return adminFetch(
    `/api/admin/agent-work/sessions/${encodeURIComponent(id)}/events`,
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
}

/**
 * POST /api/admin/agent-work/chat
 *
 * Send a natural-language chat command on behalf of an agent.
 * The backend is responsible for discovering files/jobs/datasets –
 * the frontend MUST NEVER force the user to name them.
 *
 * Payload: { agent, sessionId?, message }
 */
export async function postAgentWorkChat(payload) {
  const validated = buildAgentWorkChatPayload(payload);
  if (!validated.valid) {
    return { data: null, error: validated.error, status: 0, meta: {} };
  }
  // v0 design agent requests need a longer client timeout because the backend
  // allows up to 90 s for design_context and 150 s for full_system_context.
  // When the request payload includes an explicit contextType, pick the
  // matching constant so the browser never aborts before the backend responds.
  // When no contextType is present the conservative V0_AGENT_TIMEOUT_MS ceiling
  // (170 s) is used so full_system_context requests always succeed.
  const isV0 = validated.payload.agentName === "v0";
  const v0ContextType = validated.payload.context?.contextType ?? null;
  const timeoutMs = isV0
    ? (v0ContextType === "design_context" ? V0_DESIGN_CONTEXT_TIMEOUT_MS : V0_AGENT_TIMEOUT_MS)
    : AGENT_TIMEOUT_MS;
  const effectiveTimeoutMs =
    isV0 && v0ContextType === "full_system_context"
      ? V0_FULL_SYSTEM_CONTEXT_TIMEOUT_MS
      : timeoutMs;
  const token = await getAccessToken();
  return apiFetch("/api/admin/agent-work/chat", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(validated.payload),
  }, effectiveTimeoutMs);
}

/** POST /api/admin/agent-work/sessions/:id/cancel */
export async function cancelAgentWorkSessionV2(id) {
  if (!id) return { data: null, error: "Keine Session-ID angegeben.", status: 0, meta: {} };
  const token = await getAccessToken();
  return apiFetch(
    `/api/admin/agent-work/sessions/${encodeURIComponent(id)}/cancel`,
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
}

/** POST /api/admin/agent-work/sessions/:id/retry */
export async function retryAgentWorkSessionV2(id) {
  if (!id) return { data: null, error: "Keine Session-ID angegeben.", status: 0, meta: {} };
  const token = await getAccessToken();
  return apiFetch(
    `/api/admin/agent-work/sessions/${encodeURIComponent(id)}/retry`,
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
}

// ── System Awareness (Step 2) ──────────────────────────────────────────

/**
 * Fetch the current system-awareness snapshot from the backend.
 * GET /api/admin/system-awareness
 *
 * Returns a list of recognised system targets, recommended agent, confidence
 * and a human-readable reason.  404 / 501 are handled gracefully.
 *
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function fetchSystemAwareness() {
  return adminFetch("/api/admin/system-awareness", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * Resolve a set of awareness targets against the live system state.
 * POST /api/admin/system-awareness/resolve
 *
 * @param {object} payload  e.g. { targets: string[], agentId?: string, sessionId?: string }
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function resolveSystemAwarenessTargets(payload) {
  if (!payload || typeof payload !== "object") {
    return { data: null, error: "Ungültige Awareness-Anfrage.", status: 0, meta: {} };
  }
  const token = await getAccessToken();
  return apiFetch(
    "/api/admin/system-awareness/resolve",
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
}

/**
 * POST /api/admin/agent-work/sessions/:id/execute-approved
 *
 * Executes a double-approved agent work session on the GitHub main branch.
 * Only allowed when both Claude and OpenAI have approved the change.
 */
export async function executeApprovedAgentWorkSession(id) {
  if (!id) return { data: null, error: "Keine Session-ID angegeben.", status: 0, meta: {} };
  const token = await getAccessToken();
  return apiFetch(
    `/api/admin/agent-work/sessions/${encodeURIComponent(id)}/execute-approved`,
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
    AGENT_TIMEOUT_MS,
  );
}

// ── Agent Sentinel / Continuous Observer (Step 3) ─────────────────────────
//
// The Sentinel is a background observer that continuously monitors all six
// agent areas.  All three endpoints are admin-only and same-origin – no
// secrets are ever sent to the browser.

/**
 * Fetch the current Sentinel status (active/inactive, last scan time, issue count).
 * GET /api/admin/agent-sentinel/status
 *
 * Returns:
 *   { active: boolean, lastScan: string|null, issueCount: number,
 *     status: "active"|"inactive"|"scanning"|"error", agentScope: string|null }
 *
 * 404 / 501 are treated as "not yet deployed" and handled gracefully.
 */
export async function fetchAgentSentinelStatus() {
  return adminFetch("/api/admin/agent-sentinel/status", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * Trigger an immediate Sentinel scan across all agent areas.
 * POST /api/admin/agent-sentinel/scan
 *
 * Returns:
 *   { success: boolean, message: string, issueCount: number,
 *     issues: Array<{ area, component, risk, recommendedAgent, nextStep, evidence }>,
 *     scannedAt: string }
 *
 * Errors are returned as structured objects, never thrown.
 * No secrets are included in the payload.
 */
export async function runAgentSentinelScan() {
  const token = await getAccessToken();
  return apiFetch(
    "/api/admin/agent-sentinel/scan",
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
    AGENT_TIMEOUT_MS,
  );
}

/**
 * Fetch recently detected Sentinel issues (for the details drawer).
 * GET /api/admin/agent-sentinel/recent
 *
 * Returns:
 *   { issues: Array<{ area, component, risk, recommendedAgent, nextStep, evidence }>,
 *     total: number, detectedAt: string|null }
 *
 * 404 / 501 are treated as "not yet deployed" and handled gracefully.
 */
export async function fetchRecentAgentSentinelIssues() {
  return adminFetch("/api/admin/agent-sentinel/recent", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * POST /api/admin/agent-sentinel/scan-and-route
 *
 * Step 4: scan and route the result to the most relevant agent area.
 * Falls back gracefully to runAgentSentinelScan() when this endpoint is
 * not yet deployed (use isScanAndRouteUnavailable() from agentWorkChatLogic
 * to decide).
 *
 * @param {object} [body] – optional payload, e.g. { agentId: "deepseek" }
 */
export async function postAgentSentinelScanAndRoute(body = {}) {
  const token = await getAccessToken();
  return apiFetch(
    "/api/admin/agent-sentinel/scan-and-route",
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
    AGENT_TIMEOUT_MS,
  );
}

// ── Agent Sentinel – Persistent Layer (Step 5) ──────────────────────���─────
//
// These endpoints persist Sentinel scan results in the backend so issues
// survive across sessions. The UI shows them discreetly in a compact status
// line and in the Details Drawer – never as a full main-view table.

/**
 * Fetch all persisted Sentinel issues (with optional filters).
 * GET /api/admin/agent-sentinel/issues
 *
 * @param {object} [filters] – e.g. { status: "open", agentId: "deepseek", limit: 5 }
 * Returns: { issues: Array, total: number, fetchedAt: string }
 */
export async function fetchAgentSentinelIssues(filters = {}) {
  const query = buildQuery(filters);
  return adminFetch(`/api/admin/agent-sentinel/issues${query}`, MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * Fetch a single persisted Sentinel issue by ID.
 * GET /api/admin/agent-sentinel/issues/:id
 *
 * Returns: { issue: { id, area, component, risk, status, recommendedAgent, nextStep, evidence, executionReady } }
 */
export async function fetchAgentSentinelIssue(id) {
  if (!id) return { data: null, error: "Keine Issue-ID angegeben.", status: 0, meta: {} };
  return adminFetch(`/api/admin/agent-sentinel/issues/${encodeURIComponent(id)}`, MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * Dismiss (hide) a persisted Sentinel issue.
 * POST /api/admin/agent-sentinel/issues/:id/dismiss
 *
 * @param {string} id      – issue ID
 * @param {string} [reason] – optional short German reason text
 * Returns: { success: boolean, message: string }
 */
export async function dismissAgentSentinelIssue(id, reason = "") {
  if (!id) return { data: null, error: "Keine Issue-ID angegeben.", status: 0, meta: {} };
  const token = await getAccessToken();
  return apiFetch(
    `/api/admin/agent-sentinel/issues/${encodeURIComponent(id)}/dismiss`,
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason: reason || "Manuell ausgeblendet." }),
    },
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
}

/**
 * Trigger repair preparation for a specific persisted issue.
 * POST /api/admin/agent-sentinel/issues/:id/prepare-repair
 *
 * Returns: { success: boolean, message: string, issueId: string,
 *            status: "preparing"|"queued"|"error",
 *            agentAssigned: string|null, note: string|null }
 *
 * Does NOT execute any change – only prepares a repair proposal.
 */
export async function prepareAgentSentinelIssueRepair(id) {
  if (!id) return { data: null, error: "Keine Issue-ID angegeben.", status: 0, meta: {} };
  const token = await getAccessToken();
  return apiFetch(
    `/api/admin/agent-sentinel/issues/${encodeURIComponent(id)}/prepare-repair`,
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
    AGENT_TIMEOUT_MS,
  );
}

/**
 * Trigger a scan that also persists results in the backend.
 * POST /api/admin/agent-sentinel/scan-and-persist
 *
 * Returns: { success: boolean, message: string,
 *            newIssues: number, updatedIssues: number, issueCount: number,
 *            issues: Array, scannedAt: string }
 *
 * Falls back gracefully to runAgentSentinelScan() when this endpoint is
 * not yet deployed (check with isScanAndRouteUnavailable()).
 */
export async function runAgentSentinelScanAndPersist() {
  const token = await getAccessToken();
  return apiFetch(
    "/api/admin/agent-sentinel/scan-and-persist",
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
    AGENT_TIMEOUT_MS,
  );
}

/**
 * Fetch the current persistent Sentinel repair queue.
 * GET /api/admin/agent-sentinel/repair-queue
 *
 * Returns: { items: Array<{ id, issueId, status, agentAssigned, createdAt }>,
 *            total: number, fetchedAt: string }
 *
 * 404 / 501 are treated as "not yet deployed" and handled gracefully.
 */
export async function fetchAgentSentinelRepairQueue() {
  return adminFetch("/api/admin/agent-sentinel/repair-queue", MEDIUM_ADMIN_TIMEOUT_MS);
}

// ── Step 6: Sentinel Approval & Execution Gate ────────────────────────────

/**
 * Start the approval process for a specific Sentinel issue.
 * POST /api/admin/agent-sentinel/issues/:id/approval/start
 *
 * Returns: { success: boolean, message: string, issueId: string,
 *            approvalStarted: boolean, status: string }
 *
 * Initiates repair proposal preparation. Does NOT execute any change.
 */
export async function startSentinelIssueApproval(issueId) {
  if (!issueId) return { data: null, error: "Keine Issue-ID angegeben.", status: 0, meta: {} };
  const token = await getAccessToken();
  return apiFetch(
    `/api/admin/agent-sentinel/issues/${encodeURIComponent(issueId)}/approval/start`,
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
    AGENT_TIMEOUT_MS,
  );
}

/**
 * Trigger a Claude review for a specific Sentinel issue.
 * POST /api/admin/agent-sentinel/issues/:id/approval/claude
 *
 * Returns: { success: boolean, message: string, issueId: string,
 *            claude: { status: "approved"|"rejected", reason: string|null } }
 *
 * Claude reviews the repair proposal. Does NOT execute any change.
 */
export async function runSentinelIssueClaudeReview(issueId) {
  if (!issueId) return { data: null, error: "Keine Issue-ID angegeben.", status: 0, meta: {} };
  const token = await getAccessToken();
  return apiFetch(
    `/api/admin/agent-sentinel/issues/${encodeURIComponent(issueId)}/approval/claude`,
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
    AGENT_TIMEOUT_MS,
  );
}

/**
 * Trigger an OpenAI decision for a specific Sentinel issue.
 * POST /api/admin/agent-sentinel/issues/:id/approval/openai
 *
 * Returns: { success: boolean, message: string, issueId: string,
 *            openai: { status: "approved"|"rejected", reason: string|null } }
 *
 * OpenAI prioritises and decides on the repair. Does NOT execute any change.
 */
export async function runSentinelIssueOpenAiDecision(issueId) {
  if (!issueId) return { data: null, error: "Keine Issue-ID angegeben.", status: 0, meta: {} };
  const token = await getAccessToken();
  return apiFetch(
    `/api/admin/agent-sentinel/issues/${encodeURIComponent(issueId)}/approval/openai`,
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
    AGENT_TIMEOUT_MS,
  );
}

/**
 * Evaluate all approval gates for a specific Sentinel issue (Claude + OpenAI + Safety).
 * POST /api/admin/agent-sentinel/issues/:id/approval/evaluate
 *
 * Returns: { success: boolean, message: string, issueId: string,
 *            executionReady: boolean,
 *            claude: { status: string }, openai: { status: string },
 *            safetyGate: { status: string }, blockReason: string|null }
 *
 * executionReady=true means all gates passed and the issue can be executed.
 */
export async function evaluateSentinelIssueApproval(issueId) {
  if (!issueId) return { data: null, error: "Keine Issue-ID angegeben.", status: 0, meta: {} };
  const token = await getAccessToken();
  return apiFetch(
    `/api/admin/agent-sentinel/issues/${encodeURIComponent(issueId)}/approval/evaluate`,
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
    AGENT_TIMEOUT_MS,
  );
}

/**
 * Execute an approved Sentinel issue repair on main.
 * POST /api/admin/agent-sentinel/issues/:id/execute
 *
 * IMPORTANT: This must only be called when the backend has returned
 * executionReady=true via evaluateSentinelIssueApproval.
 * Frontend MUST NOT call this based on local state inference.
 *
 * Returns: { success: boolean, message: string, issueId: string,
 *            commitSha: string|null, events: string[], status: string }
 */
export async function executeSentinelIssue(issueId) {
  if (!issueId) return { data: null, error: "Keine Issue-ID angegeben.", status: 0, meta: {} };
  const token = await getAccessToken();
  return apiFetch(
    `/api/admin/agent-sentinel/issues/${encodeURIComponent(issueId)}/execute`,
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
    AGENT_TIMEOUT_MS,
  );
}

/**
 * Fetch the approval summary for a specific Sentinel issue.
 * GET /api/admin/agent-sentinel/issues/:id/approval-summary
 *
 * Returns: { issueId: string, repairReady: boolean,
 *            claude: { status: string }, openai: { status: string },
 *            safetyGate: { status: string },
 *            execution: { status: string },
 *            executionReady: boolean, blockReason: string|null }
 */
export async function fetchSentinelIssueApprovalSummary(issueId) {
  if (!issueId) return { data: null, error: "Keine Issue-ID angegeben.", status: 0, meta: {} };
  return adminFetch(
    `/api/admin/agent-sentinel/issues/${encodeURIComponent(issueId)}/approval-summary`,
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
}

// ── Step 7: Sentinel Live Status & Polling ───────────────────────────────────

/**
 * Fetch the live status of a specific Sentinel issue.
 * GET /api/admin/agent-sentinel/issues/:id/live-status
 *
 * Optimised for polling: includes approval gates, execution status and
 * executionReady in a single snapshot. Used by AgentWorkChat to automatically
 * update the chat status line and inject status bubbles on real changes.
 *
 * On any network or backend error, normalises to a friendly German error.
 * The caller must NEVER infer executionReady from the polling result.
 *
 * @param {string} issueId
 */
export async function fetchSentinelIssueLiveStatus(issueId) {
  if (!issueId) {
    return { data: null, error: "Status konnte gerade nicht geladen werden.", status: 0, meta: {} };
  }
  const result = await adminFetch(
    `/api/admin/agent-sentinel/issues/${encodeURIComponent(issueId)}/live-status`,
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
  if (result.error && !result.data) {
    return { ...result, error: "Status konnte gerade nicht geladen werden." };
  }
  return result;
}

/**
 * Fetch the live repair queue (optimised for polling; may use backend cache).
 * GET /api/admin/agent-sentinel/repair-queue/live
 *
 * Returns a lightweight snapshot of the repair queue suitable for the
 * sentinel bar live line ("Wächter: Ausführung läuft" etc.).
 */
export async function fetchSentinelRepairQueueLive() {
  const result = await adminFetch(
    "/api/admin/agent-sentinel/repair-queue/live",
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
  if (result.error && !result.data) {
    return { ...result, error: "Live-Status ist momentan nicht erreichbar." };
  }
  return result;
}

/**
 * Trigger an immediate backend refresh of the issue status.
 * POST /api/admin/agent-sentinel/issues/:id/refresh-status
 *
 * Called when the user explicitly requests a status refresh or when polling
 * detects a stale state that needs immediate resolution.
 *
 * @param {string} issueId
 */
export async function refreshSentinelIssueStatus(issueId) {
  if (!issueId) {
    return { data: null, error: "Status konnte gerade nicht geladen werden.", status: 0, meta: {} };
  }
  const token = await getAccessToken();
  const result = await apiFetch(
    `/api/admin/agent-sentinel/issues/${encodeURIComponent(issueId)}/refresh-status`,
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
  if (result.error && !result.data) {
    return { ...result, error: "Status konnte gerade nicht geladen werden." };
  }
  return result;
}

// ── Step 8: Notification & History Layer ─────────────────────────────────────

/**
 * Fetch the current Sentinel notification summary.
 * GET /api/admin/agent-sentinel/notifications
 *
 * Returns a lightweight summary of open, critical, waiting and
 * ready_for_execution counts used by the sentinel bar notification line.
 *
 * Errors are normalised to a friendly German message; never raw JSON.
 */
export async function fetchAgentSentinelNotifications() {
  const result = await adminFetch(
    "/api/admin/agent-sentinel/notifications",
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
  if (result.error && !result.data) {
    return { ...result, error: "Benachrichtigungen konnten gerade nicht geladen werden." };
  }
  return result;
}

/**
 * Fetch the change history for a specific Sentinel issue.
 * GET /api/admin/agent-sentinel/issues/:id/history
 *
 * Returns a list of history events suitable for compact timeline display.
 * Never called automatically; only triggered by explicit user action.
 *
 * Errors are normalised to a friendly German message; never raw JSON.
 *
 * @param {string} issueId
 */
export async function fetchAgentSentinelIssueHistory(issueId) {
  if (!issueId) {
    return { data: null, error: "Der Verlauf konnte gerade nicht geladen werden.", status: 0, meta: {} };
  }
  const result = await adminFetch(
    `/api/admin/agent-sentinel/issues/${encodeURIComponent(issueId)}/history`,
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
  if (result.error && !result.data) {
    return { ...result, error: "Der Verlauf konnte gerade nicht geladen werden." };
  }
  return result;
}

// ── Step 9: Sentinel Escalation & Manual Override ────────────────────────────

/**
 * Fetch all active Sentinel escalations.
 * GET /api/admin/agent-sentinel/escalations
 *
 * Errors are normalised to a friendly German message.
 */
export async function fetchAgentSentinelEscalations() {
  const result = await adminFetch(
    "/api/admin/agent-sentinel/escalations",
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
  if (result.error && !result.data) {
    return { ...result, error: "Eskalationen konnten gerade nicht geladen werden." };
  }
  return result;
}

/**
 * Escalate (raise the level of) a specific Sentinel issue.
 * POST /api/admin/agent-sentinel/issues/:id/escalate
 *
 * @param {string} issueId
 * @param {object} [payload]
 */
export async function escalateAgentSentinelIssue(issueId, payload = {}) {
  if (!issueId) {
    return { data: null, error: "Die Aktion konnte gerade nicht ausgeführt werden.", status: 0, meta: {} };
  }
  const result = await adminFetch(
    `/api/admin/agent-sentinel/issues/${encodeURIComponent(issueId)}/escalate`,
    MEDIUM_ADMIN_TIMEOUT_MS,
    { method: "POST", body: JSON.stringify(payload), headers: { "Content-Type": "application/json" } },
  );
  if (result.error && !result.data) {
    return { ...result, error: "Die Aktion konnte gerade nicht ausgeführt werden." };
  }
  return result;
}

/**
 * Pause a specific Sentinel issue (halt automatic actions on it).
 * POST /api/admin/agent-sentinel/issues/:id/pause
 *
 * @param {string} issueId
 * @param {object} [payload]
 */
export async function pauseAgentSentinelIssue(issueId, payload = {}) {
  if (!issueId) {
    return { data: null, error: "Die Aktion konnte gerade nicht ausgeführt werden.", status: 0, meta: {} };
  }
  const result = await adminFetch(
    `/api/admin/agent-sentinel/issues/${encodeURIComponent(issueId)}/pause`,
    MEDIUM_ADMIN_TIMEOUT_MS,
    { method: "POST", body: JSON.stringify(payload), headers: { "Content-Type": "application/json" } },
  );
  if (result.error && !result.data) {
    return { ...result, error: "Die Aktion konnte gerade nicht ausgeführt werden." };
  }
  return result;
}

/**
 * Resume a previously paused Sentinel issue.
 * POST /api/admin/agent-sentinel/issues/:id/resume
 *
 * @param {string} issueId
 * @param {object} [payload]
 */
export async function resumeAgentSentinelIssue(issueId, payload = {}) {
  if (!issueId) {
    return { data: null, error: "Die Aktion konnte gerade nicht ausgeführt werden.", status: 0, meta: {} };
  }
  const result = await adminFetch(
    `/api/admin/agent-sentinel/issues/${encodeURIComponent(issueId)}/resume`,
    MEDIUM_ADMIN_TIMEOUT_MS,
    { method: "POST", body: JSON.stringify(payload), headers: { "Content-Type": "application/json" } },
  );
  if (result.error && !result.data) {
    return { ...result, error: "Die Aktion konnte gerade nicht ausgeführt werden." };
  }
  return result;
}

/**
 * Request manual review for a specific Sentinel issue.
 * POST /api/admin/agent-sentinel/issues/:id/manual-review
 *
 * @param {string} issueId
 * @param {object} [payload]
 */
export async function requestAgentSentinelManualReview(issueId, payload = {}) {
  if (!issueId) {
    return { data: null, error: "Die Aktion konnte gerade nicht ausgeführt werden.", status: 0, meta: {} };
  }
  const result = await adminFetch(
    `/api/admin/agent-sentinel/issues/${encodeURIComponent(issueId)}/manual-review`,
    MEDIUM_ADMIN_TIMEOUT_MS,
    { method: "POST", body: JSON.stringify(payload), headers: { "Content-Type": "application/json" } },
  );
  if (result.error && !result.data) {
    return { ...result, error: "Die Aktion konnte gerade nicht ausgeführt werden." };
  }
  return result;
}

/**
 * Clear (reset) the escalation for a specific Sentinel issue.
 * POST /api/admin/agent-sentinel/issues/:id/clear-escalation
 *
 * @param {string} issueId
 * @param {object} [payload]
 */
export async function clearAgentSentinelEscalation(issueId, payload = {}) {
  if (!issueId) {
    return { data: null, error: "Die Aktion konnte gerade nicht ausgeführt werden.", status: 0, meta: {} };
  }
  const result = await adminFetch(
    `/api/admin/agent-sentinel/issues/${encodeURIComponent(issueId)}/clear-escalation`,
    MEDIUM_ADMIN_TIMEOUT_MS,
    { method: "POST", body: JSON.stringify(payload), headers: { "Content-Type": "application/json" } },
  );
  if (result.error && !result.data) {
    return { ...result, error: "Die Aktion konnte gerade nicht ausgeführt werden." };
  }
  return result;
}

// ── Step 10: Agent Autonomy Policy & Permission Control ──────────────────────

/**
 * Fetch all agent policies (overview).
 * GET /api/admin/agents/policies
 *
 * Returns a list of policy objects, one per agent.
 * Error is normalised to German.
 */
export async function fetchAgentPolicies() {
  const result = await adminFetch("/api/admin/agents/policies", MEDIUM_ADMIN_TIMEOUT_MS);
  if (result.error && !result.data) {
    return { ...result, error: "Policy konnte gerade nicht geladen werden." };
  }
  return result;
}

/**
 * Fetch the policy for a specific agent.
 * GET /api/admin/agents/policies/:agentName
 *
 * @param {string} agentName - agent identifier (e.g. "qwen", "deepseek")
 */
export async function fetchAgentPolicy(agentName) {
  if (!agentName) {
    return { data: null, error: "Policy konnte gerade nicht geladen werden.", status: 0, meta: {} };
  }
  const result = await adminFetch(
    `/api/admin/agents/policies/${encodeURIComponent(agentName)}`,
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
  if (result.error && !result.data) {
    return { ...result, error: "Policy konnte gerade nicht geladen werden." };
  }
  return result;
}

/**
 * Evaluate whether a policy allows a given action for an agent.
 * POST /api/admin/agents/policies/evaluate
 *
 * @param {{ agentName: string, action: string, context?: object }} payload
 */
export async function evaluateAgentPolicy(payload) {
  if (!payload || !payload.agentName || !payload.action) {
    return { data: null, error: "Die Freigaberegeln konnten gerade nicht geprüft werden.", status: 0, meta: {} };
  }
  const result = await adminFetch(
    "/api/admin/agents/policies/evaluate",
    MEDIUM_ADMIN_TIMEOUT_MS,
    { method: "POST", body: JSON.stringify(payload), headers: { "Content-Type": "application/json" } },
  );
  if (result.error && !result.data) {
    // Distinguish blocked-by-policy (status 403) from network errors
    if (result.status === 403) {
      return { ...result, error: "Diese Aktion ist nach den Sicherheitsregeln blockiert." };
    }
    return { ...result, error: "Die Freigaberegeln konnten gerade nicht geprüft werden." };
  }
  return result;
}

// ── Step 11: Agent Live-Health Check ─────────────────────────────────────────

/**
 * Run a live connectivity test for all 6 agents.
 * POST /api/admin/agents/live-health/run
 *
 * Returns a { data, error, status, meta } shape where data contains
 * per-agent results (live/error/latencyMs) – no secrets in the response.
 *
 * Uses HEAVY_ADMIN_TIMEOUT_MS because the backend fans out to 6 LLM endpoints.
 */
export async function runAgentLiveHealth() {
  const result = await adminFetch(
    "/api/admin/agents/live-health/run",
    HEAVY_ADMIN_TIMEOUT_MS,
    { method: "POST", headers: { "Content-Type": "application/json" } },
  );
  if (result.error && !result.data) {
    return { ...result, error: "Live-Test konnte gerade nicht durchgeführt werden." };
  }
  return result;
}

// ── Long-Running Agent Task Status ───────────────────────────────────────────

/**
 * Fetch the current status of a long-running agent task.
 * GET /api/admin/agent-work-chat/tasks/:taskId
 *
 * Called after POST /api/admin/agent-work/chat returns { accepted: true, taskId }
 * so the frontend can poll for the final result without being blocked by a single
 * long-lived HTTP request.
 *
 * Returns the standard { data, error, status, meta } shape where data carries:
 *   { taskId, status, phase, progressMessage, errorMessage, traceId,
 *     assistantMessage, sessionId, systemContextUsed, evidenceRefs, missingContext }
 *
 * Uses AGENT_TIMEOUT_MS so the poll itself never times out before the backend
 * responds with a status update.
 *
 * @param {string} taskId
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function fetchAgentWorkChatTaskStatus(taskId) {
  if (!taskId || typeof taskId !== "string") {
    return { data: null, error: "Keine Task-ID angegeben.", status: 0, meta: {} };
  }
  return adminFetch(
    `/api/admin/agent-work-chat/tasks/${encodeURIComponent(taskId)}`,
    AGENT_TIMEOUT_MS,
  );
}

// ── v0 → Qwen Handoff ────────────────────────────────────────────────────────

/**
 * POST /api/admin/agent-work/handoff
 *
 * Starts a Qwen implementation task based on a structured v0 UIChangeset.
 * The backend must return HTTP 202 + { taskId } on success.
 *
 * Payload:
 *   fromAgent      "v0"
 *   toAgent        "qwen"
 *   uiChangeset    raw UIChangeset object from the v0 response
 *   originalText   v0 chat bubble text (preview)
 *   fullText       complete v0 response text
 *   sourceMessageId message id of the originating v0 message
 *   sessionId      current chat session id (may be null)
 *   conversationId alias for sessionId (may be null)
 *
 * @param {{ fromAgent:string, toAgent:string, uiChangeset:object,
 *           originalText?:string, fullText?:string,
 *           sourceMessageId?:string, sessionId?:string, conversationId?:string }} payload
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function postQwenHandoff(payload) {
  if (!payload || !payload.uiChangeset || typeof payload.uiChangeset !== "object") {
    return { data: null, error: "Kein UIChangeset angegeben.", status: 0, meta: {} };
  }
  const token = await getAccessToken();
  return apiFetch("/api/admin/agent-work/handoff", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      fromAgent:      payload.fromAgent      ?? "v0",
      toAgent:        payload.toAgent        ?? "qwen",
      uiChangeset:    payload.uiChangeset,
      originalText:   payload.originalText   ?? null,
      fullText:       payload.fullText       ?? null,
      sourceMessageId: payload.sourceMessageId ?? null,
      sessionId:      payload.sessionId      ?? null,
      conversationId: payload.conversationId ?? payload.sessionId ?? null,
    }),
  }, MEDIUM_ADMIN_TIMEOUT_MS);
}

// ── VisualContext V1 ──────────────────────────────────────────────────────────

/**
 * POST /api/admin/agent-work/visual-context
 *
 * Submits a screenshot for visual analysis. The backend may:
 *  - Perform real image analysis (via multimodal provider)
 *    → response includes { analysisPerformed: true, view, regions, layoutIssues, recommendedFocus }
 *  - Fall back to metadata-only (no multimodal provider available)
 *    → response omits analysisPerformed or sets it to false
 *
 * The caller must use normalizeVisualContextSummary() to interpret the result.
 * Never display "Screenshot analysiert" to the user if analysisPerformed !== true.
 *
 * Payload:
 *   screenshots   Array of buildScreenshotPayloadEntry results (at least one required)
 *   sessionId     current chat session id (may be null)
 *
 * @param {{ screenshots: Array, sessionId?: string|null }} payload
 * @returns {Promise<{ data: object|null, error: string|null, status: number, meta: object }>}
 */
export async function postVisualContext(payload) {
  if (!payload || !Array.isArray(payload.screenshots) || payload.screenshots.length === 0) {
    return { data: null, error: "Kein Screenshot für die Analyse angegeben.", status: 0, meta: {} };
  }
  const token = await getAccessToken();
  return apiFetch("/api/admin/agent-work/visual-context", {
    method: "POST",
    _accessToken: token,
    _sameOrigin: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      screenshots: payload.screenshots,
      sessionId:   payload.sessionId ?? null,
    }),
  }, MEDIUM_ADMIN_TIMEOUT_MS);
}

// ── Agent Role Registry V1 ────────────────────────────────────────────────

/**
 * GET /api/admin/agents/registry
 *
 * Returns the full list of registered agents with their roles and capabilities.
 * If the endpoint is unavailable the caller receives { data: null, error: string }.
 */
export async function getAgentRegistry() {
  return adminFetch("/api/admin/agents/registry", MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * GET /api/admin/agents/registry/:agentId
 *
 * Returns role + capability detail for a single agent.
 *
 * @param {string} agentId
 */
export async function getAgentRegistryEntry(agentId) {
  if (!agentId || typeof agentId !== "string") {
    return { data: null, error: "Kein Agent-ID angegeben.", status: 0, meta: {} };
  }
  return adminFetch(
    `/api/admin/agents/registry/${encodeURIComponent(agentId)}`,
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
}

/**
 * GET /api/admin/agents/:agentId/system-context
 *
 * Returns the current system context for a single agent (used areas, missing areas, etc.).
 *
 * @param {string} agentId
 */
export async function getAgentSystemContext(agentId) {
  if (!agentId || typeof agentId !== "string") {
    return { data: null, error: "Kein Agent-ID angegeben.", status: 0, meta: {} };
  }
  return adminFetch(
    `/api/admin/agents/${encodeURIComponent(agentId)}/system-context`,
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
}

/**
 * POST /api/admin/agents/:agentId/can-perform
 *
 * Checks whether an agent is currently permitted to perform a given action.
 *
 * Payload: { action: string }
 *
 * @param {string} agentId
 * @param {string} action
 */
export async function postAgentCanPerform(agentId, action) {
  if (!agentId || typeof agentId !== "string") {
    return { data: null, error: "Kein Agent-ID angegeben.", status: 0, meta: {} };
  }
  if (!action || typeof action !== "string") {
    return { data: null, error: "Keine Aktion angegeben.", status: 0, meta: {} };
  }
  const token = await getAccessToken();
  return apiFetch(
    `/api/admin/agents/${encodeURIComponent(agentId)}/can-perform`,
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action }),
    },
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
}

// ── Agent Capability Router V1 ─────────────────────────────────────────────

/**
 * POST /api/admin/agents/router/classify
 *
 * Classifies an incoming user task text into a task type.
 *
 * Payload: { text: string, context?: object }
 *
 * @param {string} text     – raw user input text
 * @param {object} [context] – optional context object
 */
export async function classifyAgentTask(text, context) {
  if (!text || typeof text !== "string") {
    return { data: null, error: "Kein Text zum Klassifizieren angegeben.", status: 0, meta: {} };
  }
  const token = await getAccessToken();
  return apiFetch(
    "/api/admin/agents/router/classify",
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text, ...(context ? { context } : {}) }),
    },
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
}

/**
 * POST /api/admin/agents/router/route
 *
 * Selects the best agent + action for a given task.
 *
 * Payload: { text: string, taskType?: string, context?: object }
 *
 * @param {string} text       – raw user input text
 * @param {string} [taskType] – optional pre-classified task type
 * @param {object} [context]  – optional context object
 */
export async function routeAgentTask(text, taskType, context) {
  if (!text || typeof text !== "string") {
    return { data: null, error: "Kein Text zum Routen angegeben.", status: 0, meta: {} };
  }
  const token = await getAccessToken();
  return apiFetch(
    "/api/admin/agents/router/route",
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        text,
        ...(taskType ? { taskType } : {}),
        ...(context  ? { context  } : {}),
      }),
    },
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
}

/**
 * POST /api/admin/agents/router/explain
 *
 * Returns a human-readable explanation of a routing decision.
 *
 * Payload: { text: string, taskType?: string, targetAgent?: string }
 *
 * @param {string} text          – raw user input text
 * @param {string} [taskType]    – task type from classify step
 * @param {string} [targetAgent] – agent selected in route step
 */
export async function explainAgentRouting(text, taskType, targetAgent) {
  if (!text || typeof text !== "string") {
    return { data: null, error: "Kein Text für Routing-Erklärung angegeben.", status: 0, meta: {} };
  }
  const token = await getAccessToken();
  return apiFetch(
    "/api/admin/agents/router/explain",
    {
      method: "POST",
      _accessToken: token,
      _sameOrigin: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        text,
        ...(taskType    ? { taskType    } : {}),
        ...(targetAgent ? { targetAgent } : {}),
      }),
    },
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
}

/**
 * POST /api/admin/agents/dispatcher/dispatch
 *
 * Starts a new autonomous dispatcher run. Returns backend errors honestly.
 */
export async function dispatchAutonomousTask(payload = {}) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return { data: null, error: "Ungültiger Dispatcher-Auftrag.", status: 0, meta: {} };
  }
  return adminFetch(
    "/api/admin/agents/dispatcher/dispatch",
    MEDIUM_ADMIN_TIMEOUT_MS,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

/**
 * GET /api/admin/agents/dispatcher/runs/:autonomyRunId
 *
 * Polls a dispatcher run by autonomyRunId, or via a same-origin pollUrl when
 * the backend explicitly provides one.
 */
export async function getAutonomousDispatchRun(autonomyRunId, options = {}) {
  const pollPath = normalizeSameOriginAdminPath(options?.pollUrl ?? null);
  if (!pollPath && options?.pollUrl) {
    return { data: null, error: "Polling-URL ist ungültig.", status: 0, meta: {} };
  }
  if (!pollPath && (!autonomyRunId || typeof autonomyRunId !== "string")) {
    return { data: null, error: "Keine AutonomyRun-ID angegeben.", status: 0, meta: {} };
  }
  const path = pollPath || `/api/admin/agents/dispatcher/runs/${encodeURIComponent(autonomyRunId)}`;
  return adminFetch(path, MEDIUM_ADMIN_TIMEOUT_MS);
}

/**
 * POST /api/admin/agents/dispatcher/runs/:autonomyRunId/approve
 *
 * Freigibt einen Dispatcher-Lauf, der auf Nutzerfreigabe wartet.
 */
export async function approveAutonomousDispatchRun(autonomyRunId, payload = {}) {
  if (!autonomyRunId || typeof autonomyRunId !== "string") {
    return { data: null, error: "Keine AutonomyRun-ID angegeben.", status: 0, meta: {} };
  }
  return adminFetch(
    `/api/admin/agents/dispatcher/runs/${encodeURIComponent(autonomyRunId)}/approve`,
    MEDIUM_ADMIN_TIMEOUT_MS,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload && typeof payload === "object" ? payload : {}),
    },
  );
}

// ── Monitoring & Alerting ──────────────────────────────────────────────────

/** GET /api/admin/alerts – Alle aktiven Alerts abrufen. */
export async function fetchAdminAlerts() {
  return adminFetch("/api/admin/alerts", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** GET /api/admin/alerts/summary – Alert-Zusammenfassung (Kritisch/Warning/Info). */
export async function fetchAdminAlertsSummary() {
  return adminFetch("/api/admin/alerts/summary");
}

/** GET /api/admin/alerts/history – Alert-History (letzte 30 Tage). */
export async function fetchAdminAlertsHistory() {
  return adminFetch("/api/admin/alerts/history", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** POST /api/admin/alerts/:id/resolve – Alert als gelöst markieren. */
export async function resolveAdminAlert(id) {
  if (!id) return { data: null, error: "Keine Alert-ID angegeben.", status: 0, meta: {} };
  return adminFetch(
    `/api/admin/alerts/${encodeURIComponent(id)}/resolve`,
    MEDIUM_ADMIN_TIMEOUT_MS,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) },
  );
}

// ── API-Nutzung ────────────────────────────────────────────────────────────

/** GET /api/admin/api-usage/today – API-Nutzung heute pro Provider. */
export async function fetchAdminApiUsageToday() {
  return adminFetch("/api/admin/api-usage/today", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** GET /api/admin/api-usage/monthly – Monatliche API-Nutzung. */
export async function fetchAdminApiUsageMonthly() {
  return adminFetch("/api/admin/api-usage/monthly", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** GET /api/admin/api-usage/:provider/history – Nutzungshistorie pro Provider. */
export async function fetchAdminApiUsageProviderHistory(provider) {
  if (!provider) return { data: null, error: "Kein Provider angegeben.", status: 0, meta: {} };
  return adminFetch(
    `/api/admin/api-usage/${encodeURIComponent(provider)}/history`,
    MEDIUM_ADMIN_TIMEOUT_MS,
  );
}

// ── Pipeline Orchestrator ──────────────────────────────────────────────────

/** GET /api/pipeline/status – Status aller Pipeline-Chains. */
export async function fetchPipelineChainStatus() {
  return adminFetch("/api/pipeline/status", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** GET /api/pipeline/history – Pipeline-Ausführungshistorie. */
export async function fetchPipelineHistory() {
  return adminFetch("/api/pipeline/history", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** POST /api/pipeline/trigger/:chainName – Pipeline-Chain manuell starten. */
export async function triggerPipelineChain(chainName) {
  if (!chainName) return { data: null, error: "Kein Chain-Name angegeben.", status: 0, meta: {} };
  return adminFetch(
    `/api/pipeline/trigger/${encodeURIComponent(chainName)}`,
    HEAVY_ADMIN_TIMEOUT_MS,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) },
  );
}

// ── Architect Actions (claudeRuntimeArchitect Audit) ──────────────────────

/** GET /api/admin/architect/actions – Alle Architect-Aktionen der letzten 7 Tage. */
export async function fetchArchitectActions() {
  return adminFetch("/api/admin/architect/actions", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** POST /api/admin/architect/revert/:actionId – Architect-Aktion rückgängig machen. */
export async function revertArchitectAction(actionId) {
  if (!actionId) return { data: null, error: "Keine Aktions-ID angegeben.", status: 0, meta: {} };
  return adminFetch(
    `/api/admin/architect/revert/${encodeURIComponent(actionId)}`,
    HEAVY_ADMIN_TIMEOUT_MS,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) },
  );
}

// ── Label Approval (discovery_labels Review) ──────────────────────────────

/** GET /api/admin/labels/pending – Alle ausstehenden Labels. */
export async function fetchPendingLabels() {
  return adminFetch("/api/admin/labels/pending", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** POST /api/admin/labels/:id/approve – Label genehmigen. */
export async function approveLabel(id) {
  if (!id) return { data: null, error: "Keine Label-ID angegeben.", status: 0, meta: {} };
  return adminFetch(
    `/api/admin/labels/${encodeURIComponent(id)}/approve`,
    MEDIUM_ADMIN_TIMEOUT_MS,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) },
  );
}

/** POST /api/admin/labels/:id/reject – Label ablehnen. */
export async function rejectLabel(id) {
  if (!id) return { data: null, error: "Keine Label-ID angegeben.", status: 0, meta: {} };
  return adminFetch(
    `/api/admin/labels/${encodeURIComponent(id)}/reject`,
    MEDIUM_ADMIN_TIMEOUT_MS,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) },
  );
}

/** POST /api/admin/labels/bulk-approve – Alle ausstehenden Labels genehmigen. */
export async function bulkApproveLabels() {
  return adminFetch(
    "/api/admin/labels/bulk-approve",
    HEAVY_ADMIN_TIMEOUT_MS,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) },
  );
}

// ── Runtime Snapshots (learning_runtime_state Versioning) ─────────────────

/** GET /api/admin/runtime/snapshots – Liste der letzten Snapshots. */
export async function fetchRuntimeSnapshots() {
  return adminFetch("/api/admin/runtime/snapshots", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** POST /api/admin/runtime/rollback/:snapshotId – Rollback auf Snapshot. */
export async function rollbackRuntimeSnapshot(snapshotId) {
  if (!snapshotId) return { data: null, error: "Keine Snapshot-ID angegeben.", status: 0, meta: {} };
  return adminFetch(
    `/api/admin/runtime/rollback/${encodeURIComponent(snapshotId)}`,
    HEAVY_ADMIN_TIMEOUT_MS,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) },
  );
}

/** GET /api/admin/runtime/current – Aktueller Runtime-State. */
export async function fetchRuntimeCurrent() {
  return adminFetch("/api/admin/runtime/current", MEDIUM_ADMIN_TIMEOUT_MS);
}

// ── Performance Dashboard ─────────────────────────────────────────────────

/** GET /api/admin/prices-daily/coverage – Coverage-Metadaten der täglichen Preisdaten. */
export async function fetchAdminPricesDailyCoverage() {
  return adminFetch("/api/admin/prices-daily/coverage", DEFAULT_TIMEOUT_MS);
}

/** GET /api/admin/performance/summary?period=X – Performance-Summary für einen Zeitraum. */
export async function fetchAdminPerformanceSummary(period = "30d") {
  const validPeriods = ["7d", "30d", "90d", "180d", "all"];
  const p = validPeriods.includes(period) ? period : "30d";
  return adminFetch(`/api/admin/performance/summary?period=${encodeURIComponent(p)}`, DEFAULT_TIMEOUT_MS);
}

// ── User-Owned Data (Supabase-backed) ─────────────────────────────────────────
// These endpoints are authenticated via the Supabase session bearer token.
// They persist user data (portfolio positions, watchlist, trade journal) to
// Supabase PostgreSQL so that data survives across devices and browser sessions.

/** GET /api/user/portfolio – Alle gespeicherten Depot-Positionen des Nutzers. */
export async function fetchUserPortfolio() {
  return authenticatedFetch("/api/user/portfolio");
}

/**
 * POST /api/user/portfolio – Gesamten Satz an Depot-Positionen speichern (Batch-Upsert).
 * @param {Array} positions – Array von Position-Objekten
 */
export async function saveUserPortfolioPositions(positions) {
  return authenticatedFetch("/api/user/portfolio", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ positions: Array.isArray(positions) ? positions : [] }),
  });
}

/**
 * DELETE /api/user/portfolio?id=<uuid> – Einzelne Depot-Position löschen.
 * @param {string} id
 */
export async function deleteUserPortfolioPosition(id) {
  return authenticatedFetch(`/api/user/portfolio?id=${encodeURIComponent(id)}`, { method: "DELETE" });
}

/** GET /api/user/watchlist – Watchlist-Symbole des Nutzers. */
export async function fetchUserWatchlist() {
  return authenticatedFetch("/api/user/watchlist");
}

/**
 * POST /api/user/watchlist – Symbol zur Watchlist hinzufügen.
 * @param {string} symbol
 */
export async function addToUserWatchlist(symbol) {
  return authenticatedFetch("/api/user/watchlist", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ symbol }),
  });
}

/**
 * DELETE /api/user/watchlist?symbol=AAPL – Symbol aus der Watchlist entfernen.
 * @param {string} symbol
 */
export async function removeFromUserWatchlist(symbol) {
  return authenticatedFetch(`/api/user/watchlist?symbol=${encodeURIComponent(symbol)}`, { method: "DELETE" });
}

/** GET /api/user/trade-journal – Alle Trade-Journal-Einträge des Nutzers. */
export async function fetchUserTradeJournal() {
  return apiFetch("/api/user/trade-journal");
}

/**
 * POST /api/user/trade-journal – Neuen Trade-Journal-Eintrag speichern.
 * @param {object} entry
 */
export async function saveUserTradeJournalEntry(entry) {
  return apiFetch("/api/user/trade-journal", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ entry }),
  });
}

/**
 * DELETE /api/user/trade-journal?id=<uuid> – Einzelnen Trade-Journal-Eintrag löschen.
 * @param {string} id
 */
export async function deleteUserTradeJournalEntry(id) {
  return apiFetch(`/api/user/trade-journal?id=${encodeURIComponent(id)}`, { method: "DELETE" });
}

// ── A4.9 Dashboard Expectancy & Profitability Endpoints ──────────────────

/** GET /api/admin/agents/trusted-accuracy-split/overview (Backend v44 contract) */
export async function fetchAgentTrustedAccuracyOverview() {
  return adminFetch("/api/admin/agents/trusted-accuracy-split/overview", MEDIUM_ADMIN_TIMEOUT_MS);
}

// NOTE (Backend v44 alignment): there is no dedicated
// "/api/admin/agents/trusted-accuracy/summary" (or "-split/summary") contract
// on the backend — "trusted-accuracy-split/overview" already returns the full
// report. Rather than calling a route that is guaranteed to 404, this
// resolves client-side as not-deployed so callers render "NICHT DEPLOYED"
// instead of a fake LIVE state.
export async function fetchAgentTrustedAccuracySummary() {
  return { data: null, error: null, status: 501, meta: { notImplemented: true, reason: "no_backend_v44_contract" } };
}

/** GET /api/admin/agents/trusted-expectancy/overview */
export async function fetchAgentTrustedExpectancyOverview() {
  return adminFetch("/api/admin/agents/trusted-expectancy/overview", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** GET /api/admin/agents/trusted-expectancy/profitability */
export async function fetchAgentTrustedExpectancyProfitability(params = {}) {
  const query = buildQuery(params);
  return adminFetch(`/api/admin/agents/trusted-expectancy/profitability${query}`, MEDIUM_ADMIN_TIMEOUT_MS);
}

/** GET /api/admin/agents/trusted-expectancy/breakdowns */
export async function fetchAgentTrustedExpectancyBreakdowns() {
  return adminFetch("/api/admin/agents/trusted-expectancy/breakdowns", MEDIUM_ADMIN_TIMEOUT_MS);
}

/** GET /api/admin/agents/trusted-expectancy/missing-returns */
export async function fetchAgentTrustedExpectancyMissingReturns() {
  return adminFetch("/api/admin/agents/trusted-expectancy/missing-returns", MEDIUM_ADMIN_TIMEOUT_MS);
}

// ── Forecast Verification Repair ──────────────────────────────────────────

export async function fetchForecastVerificationRepairOverview() {
  return adminFetch("/api/admin/agents/forecast-verification-repair/overview", MEDIUM_ADMIN_TIMEOUT_MS);
}

export async function fetchForecastVerificationRepairPlan() {
  return adminFetch("/api/admin/agents/forecast-verification-repair/plan", MEDIUM_ADMIN_TIMEOUT_MS);
}

export async function fetchForecastVerificationRepairDueOverdue() {
  return adminFetch("/api/admin/agents/forecast-verification-repair/due-overdue", MEDIUM_ADMIN_TIMEOUT_MS);
}

export async function fetchForecastVerificationRepairBlocked() {
  return adminFetch("/api/admin/agents/forecast-verification-repair/blocked", MEDIUM_ADMIN_TIMEOUT_MS);
}

export async function fetchForecastVerificationRepairAccuracyPreview() {
  return adminFetch("/api/admin/agents/forecast-verification-repair/accuracy-preview", MEDIUM_ADMIN_TIMEOUT_MS);
}

export async function fetchForecastVerificationRepairLastRun() {
  return adminFetch("/api/admin/agents/forecast-verification-repair/last-run", MEDIUM_ADMIN_TIMEOUT_MS);
}

export async function fetchForecastVerificationRepairSafetyInvariants() {
  return adminFetch("/api/admin/agents/forecast-verification-repair/safety-invariants", MEDIUM_ADMIN_TIMEOUT_MS);
}

export async function runForecastVerificationRepairDryRun() {
  return adminFetch("/api/admin/agents/forecast-verification-repair/run-all", MEDIUM_ADMIN_TIMEOUT_MS, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mode: "dryRun" }),
  });
}

// ── Backfill Pilot Status ──���────────────────────────────────────────────

export async function fetchBackfillPilotStatus() {
  return adminFetch("/api/admin/agents/backfill/pilot-status", MEDIUM_ADMIN_TIMEOUT_MS);
}

// ── Agent Takeover Readiness ────────────────────────────────────────────

export async function fetchAgentTakeoverReadiness() {
  return adminFetch("/api/admin/agents/operator/execution-readiness", MEDIUM_ADMIN_TIMEOUT_MS);
}

// ── HQS Demo Portfolio Integration ──────────────────────────────────────────
// Paper-only trading system with Daily Runner, Paper Execution, and OpenAI Manager Agent.
// All endpoints are Admin-only and accessed via same-origin proxy (api/admin-proxy.js).

// Daily Runner Endpoints
export async function getDemoPortfolioDailyOverview() {
  return adminFetch("/api/admin/demo-portfolio/daily-runner/overview", MEDIUM_ADMIN_TIMEOUT_MS);
}

export async function getDemoPortfolioDailyPreflight() {
  return adminFetch("/api/admin/demo-portfolio/daily-runner/preflight", MEDIUM_ADMIN_TIMEOUT_MS);
}

export async function getDemoPortfolioDailyPlan() {
  return adminFetch("/api/admin/demo-portfolio/daily-runner/plan", MEDIUM_ADMIN_TIMEOUT_MS);
}

export async function getDemoPortfolioDailyLastRun() {
  return adminFetch("/api/admin/demo-portfolio/daily-runner/last-run", MEDIUM_ADMIN_TIMEOUT_MS);
}

export async function getDemoPortfolioDailyHistory() {
  return adminFetch("/api/admin/demo-portfolio/daily-runner/history", MEDIUM_ADMIN_TIMEOUT_MS);
}

export async function getDemoPortfolioDailySafety() {
  return adminFetch("/api/admin/demo-portfolio/daily-runner/safety", MEDIUM_ADMIN_TIMEOUT_MS);
}

export async function getDemoPortfolioAfterSellTracking() {
  return adminFetch("/api/admin/demo-portfolio/daily-runner/after-sell-tracking", MEDIUM_ADMIN_TIMEOUT_MS);
}

export async function runDemoPortfolioDailyRunner(payload) {
  return adminFetch("/api/admin/demo-portfolio/daily-runner/run", MEDIUM_ADMIN_TIMEOUT_MS, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

// Paper Execution Endpoints
export async function getPaperExecutionOverview() {
  return adminFetch("/api/admin/demo-portfolio/paper-execution/overview", MEDIUM_ADMIN_TIMEOUT_MS);
}

export async function getPaperExecutionPlan() {
  return adminFetch("/api/admin/demo-portfolio/paper-execution/plan", MEDIUM_ADMIN_TIMEOUT_MS);
}

export async function getPaperExecutionSafety() {
  return adminFetch("/api/admin/demo-portfolio/paper-execution/safety", MEDIUM_ADMIN_TIMEOUT_MS);
}

export async function getPaperExecutionLastRun() {
  return adminFetch("/api/admin/demo-portfolio/paper-execution/last-run", MEDIUM_ADMIN_TIMEOUT_MS);
}

export async function runPaperExecution(payload) {
  return adminFetch("/api/admin/demo-portfolio/paper-execution/run", MEDIUM_ADMIN_TIMEOUT_MS, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

// OpenAI Portfolio Manager Agent Endpoints
export async function getOpenAiPortfolioOverview() {
  return adminFetch("/api/admin/demo-portfolio/openai-manager-agent/overview", MEDIUM_ADMIN_TIMEOUT_MS);
}

export async function getOpenAiPortfolioProposals() {
  return adminFetch("/api/admin/demo-portfolio/openai-manager-agent/proposals", MEDIUM_ADMIN_TIMEOUT_MS);
}

export async function getOpenAiPositionSizing() {
  return adminFetch("/api/admin/demo-portfolio/openai-manager-agent/position-sizing", MEDIUM_ADMIN_TIMEOUT_MS);
}

export async function getOpenAiBuyCandidatesWithSizing() {
  return adminFetch("/api/admin/demo-portfolio/openai-manager-agent/buy-candidates-with-sizing", MEDIUM_ADMIN_TIMEOUT_MS);
}

export async function getOpenAiValidation(symbol) {
  if (!symbol || typeof symbol !== "string") {
    return { data: null, error: "Symbol erforderlich", status: 400, meta: {} };
  }
  return adminFetch(`/api/admin/demo-portfolio/openai-manager-agent/validation/${encodeURIComponent(symbol)}`, MEDIUM_ADMIN_TIMEOUT_MS);
}

export async function getOpenAiForecastCalibration(symbol) {
  if (!symbol || typeof symbol !== "string") {
    return { data: null, error: "Symbol erforderlich", status: 400, meta: {} };
  }
  return adminFetch(`/api/admin/demo-portfolio/openai-manager-agent/forecast-calibration/${encodeURIComponent(symbol)}`, MEDIUM_ADMIN_TIMEOUT_MS);
}

export async function fetchSystemAuditOverview() {
  return adminFetch("/api/admin/system-audit/overview");
}

export async function fetchLiveProof() {
  return adminFetch("/api/admin/system-audit/live-proof");
}
